// @ts-nocheck — Phase 1A: legacy code, types added incrementally
//
// src/views/ForecastMarket.tsx — the dashboard's main view. Renders the
// forecast for whichever market is selected (today only Italy is live, but
// the file no longer hardcodes the country — it reads from window.MARKET_CONFIG,
// set by either the /forecast/italy route's implicit fallback or the
// /market/:marketId MarketDashboard wrapper).
//
// History: file was originally `legacy/app.tsx`, then `src/legacy/app.tsx`,
// then `src/views/ForecastItaly.tsx`, finally renamed to `ForecastMarket.tsx`
// in Phase H (the file's logic had already been market-agnostic since
// Phase 7.5b; the old name was misleading). The internal exported component
// is still `Dashboard` for back-compat with window.Dashboard publishes.
import React from "react";
import ReactDOM from "react-dom/client";
// Phase 4 sub-step 2: KPI strip ported to composed component using v2 KPITile.
// Imported here so JSX `<KpiStrip>` below resolves to the new ESM module
// (shadowing the legacy window.KpiStrip globaling, which no longer exists).
import KpiStrip from "../components/composed/KpiStrip";
// Phase C1 — Hero KPI sits above KpiStrip and answers the dashboard's
// single biggest question ("am I on track to hit the FY target?") with
// numbers this view already computes (currentMrr / targetMrr / aheadPct /
// trackStatus / monthsLeft / gap).
import HeroKpi from "../components/composed/HeroKpi";
// Phase G — Compare Scenarios surface. Reads the three pre-computed
// scenario row-arrays (compareScenarios.base / .optimistic / .pessimistic)
// already produced by this view's useMemo and summarises each into a
// side-by-side card. No new math; same data the chart's comparison lines
// already use, so the two surfaces always agree.
import CompareScenarios from "../components/composed/CompareScenarios";
// Phase 4 sub-step 3: GrowthChart extracted to composed/. Same window-globals
// (TOKENS, MONTH_LABELS_*, __fmtMoneyMrr) — still set by legacy primitives.tsx.
import GrowthChart from "../components/composed/GrowthChart";
// Phase 4 sub-step 4: ForecastTable relocated to composed/. Still reads
// window.Card / SectionHeader / Button / EditableNumber / useCanEdit at
// render time — all published by this file's bottom block + primitives.tsx.
import ForecastTable from "../components/composed/ForecastTable";
// Phase 4 sub-step 5: DealsView + DealPopover relocated to composed/.
// Reads window.{InfoIcon,Tooltip,MultiSelectDropdown,EmptyState,Segmented}
// + lib/model globals at render time.
import DealsView from "../components/composed/DealsView";
// Phase 4 sub-step 6: InputsPanel + SoundPickerModal relocated to composed/.
// Reads window.{Button,EditableNumber,useEscapeClose,useFocusTrap,NOTIF_*}.
import InputsPanel, { SoundPickerModal } from "../components/composed/InputsPanel";
// Phase 4 sub-step 8: NotificationBell extracted to composed/. Imports
// CHANNEL_LABELS/stageDisplay from lib/channels (shared with DealsView).
import NotificationBell from "../components/composed/NotificationBell";
// Phase 4 sub-step 7: scenarios bar extracted from the inline head__scenari
// JSX. The editor modal stays in InputsPanel (already composed in sub-step 6).
import ScenariosBar from "../components/composed/ScenariosBar";
// Phase 4 sub-step 10a: ae-chart relocated to composed/AEChart.tsx.
import AEComparisonChart from "../components/composed/AEChart";
// Phase 5.4: soft-identity picker shown after password gate when no
// viewer is selected. useViewer() reads from <ViewerProvider> in main.tsx.
import UserPicker from "../components/composed/UserPicker";
import { useViewer } from "../lib/data/viewer";
// Phase 4 sub-step 1 (scoped): identity chip in the sticky header — name +
// label + Switch button. Replaces the DevTools workaround for changing user.
import ViewerChip from "../components/composed/ViewerChip";
// Phase 3f — chart legend swatches route through the v2 Swatch primitive.
import V2Swatch from "../components/ui/Swatch";
// Phase 4d (DR-14) — migrate the few remaining native title= attrs that
// still ship in production v2 (section toggle button + hs-saved/hs-fetched
// status indicators) onto the v2 Tooltip primitive.
import Tooltip from "../components/ui/Tooltip";
// Phase 4e — publish state.lang to the rest of the app (admin views,
// primitives' lang fallback) whenever it changes here.
import { publishLang } from "../lib/lang";
// Phase B2 — theme is now owned by lib/theme.ts (so the topbar can
// expose its own toggle on non-forecast routes). ForecastItaly's old
// useState(getInitialTheme) + persist-on-change useEffect is replaced
// by useTheme(), which mirrors the prior behavior under the hood and
// stays in sync across surfaces via storage + custom events.
import { useTheme } from "../lib/theme";
// PR 1 (design system) — useDesign returns [design, setDesign] for the
// 4-preset switcher (refined / editorial / material / pill). Settings
// dropdown gets an inline picker so users can A/B compare designs live.
import { useDesign, DESIGNS, DESIGN_LABELS } from "../lib/design";
// PR 2 — lang dropdown menu (was IT|EN segmented). Same component as
// the topbar uses on non-forecast routes, so behavior is consistent.
import LangMenu from "../components/shell/LangMenu";
// Phase 4 sub-step 8 cleanup: CHANNEL_LABELS / STAGE_LABELS / stageDisplay
// moved to lib/channels.ts. The Dashboard's deal-event useEffect still
// reads STAGE_LABELS — import here so it resolves after the splice.
import { CHANNEL_LABELS, STAGE_LABELS, stageDisplay } from "../lib/channels";
// Phase B4b — layout primitives that own the bespoke .head chrome now.
// PageHeader carries title (country + flag) + meta (date + savedAt + HubSpot
// sync). Toolbar wraps ROW 2 (value-mode + scenarios + timeframe). The
// .head outer wrapper stays for scroll behavior; only its contents change.
import PageHeader from "../components/ui/PageHeader";
import Toolbar from "../components/ui/Toolbar";
/* global React, ReactDOM */
const { useState, useMemo, useEffect, useRef, createContext, useContext } = React;

// Pure utility hooks + helpers live in hooks.jsx (loaded before this script).
// Destructure them up front so the rest of app.jsx can use bare names. When
// hooks.jsx is missing (CDN failure / load order regression), these are
// undefined and any usage will throw — better than silently wrong behavior.
//
// Phase B sub-PR 2 (2026-05-10): extracted from app.jsx. See ROLLOUT.md §4.
const {
  useEscapeClose,
  useOutsideClick,
  useFocusTrap,
  deepClone,
  safeDate,
  safeDateMs,
  validateOrFallback,
} = window;

// Safe localStorage helpers — never throw. Important because:
//   1. Private mode in some browsers throws on any access
//   2. Quota-exceeded throws on setItem (especially the custom audio recording, base64 large)
//   3. Corrupted JSON in storage shouldn't crash the boot
// Use these everywhere instead of direct localStorage calls.
//
// Note: these stay in app.jsx for now. Phase B sub-PR 1 (state-storage.jsx)
// will move them along with the localStorage/KV save effects they belong to.
const lsGet = (k, fallback = null) => {
  try { const v = localStorage.getItem(k); return v == null ? fallback : v; }
  catch { return fallback; }
};
const lsSet = (k, v) => {
  try { localStorage.setItem(k, v); return true; }
  catch (e) { console.warn(`lsSet(${k}) failed:`, e?.message); return false; }
};
const lsRemove = (k) => { try { localStorage.removeItem(k); } catch {} };
const lsGetJSON = (k, fallback = null) => {
  const raw = lsGet(k);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
};
const lsSetJSON = (k, value) => {
  try { return lsSet(k, JSON.stringify(value)); } catch { return false; }
};

// runStateMigrations — moved to state-storage.jsx (Phase B sub-PR 1) along
// with the localStorage + KV persistence effects that are its only callers.

// model.jsx defines window.__EUR_MRR_PURE / __FMT_MONEY_MRR_PURE — the IMMUTABLE original
// MRR formatters. We always rewrap from those, never from window.eur (which may already be
// wrapped from a previous render). This was the cause of the ×144 ARR stacking bug.

// === Worker config ===
// Default points at the production Cloudflare Worker. This guarantees the password gate
// is always shown to a fresh visitor (without it, hubspotEnabled would be false and the
// dashboard would render unprotected). Override at runtime with ?api=... if needed.
const DEFAULT_WORKER_URL = "https://fonio-forecast-worker-v2.fonio-marco.workers.dev";
const WORKER_URL = (() => {
  try {
    const fromQuery = new URLSearchParams(location.search).get("api");
    if (fromQuery) {
      localStorage.setItem("forecast_worker_url", fromQuery);
      return fromQuery;
    }
    const stored = localStorage.getItem("forecast_worker_url");
    if (stored) return stored;
  } catch (e) {}
  return DEFAULT_WORKER_URL;
})();

// === EditMode context — gates all editable inputs ===
// Value can be:
//   - true       → fully editable (EDIT mode unlocked)
//   - false      → read-only (VIEW mode, default)
//   - {ae: true} → partial: only AE-related cells in current+future months are editable
const EditModeContext = createContext(false);
const useEditMode = () => useContext(EditModeContext);
// Hook used by editable inputs. Returns true if the input should accept clicks.
// `kind` is "ae" for AE-target/AE-real cells; anything else = generic editable.
// `monthKey` is the row's month (used to enforce "current + future" rule for AE in VIEW).
function useCanEdit(kind, monthKey) {
  const mode = useEditMode();
  if (mode === true) return true;
  if (mode && mode.ae && kind === "ae") {
    // AE editing in VIEW mode allowed only for current month and beyond.
    if (!monthKey) return true; // no month context → allow (e.g. AE name field)
    const order = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
    const todayIdx = new Date().getMonth();
    const monthIdx = order.indexOf(monthKey);
    return monthIdx >= todayIdx;
  }
  return false;
}

function relativeTime(iso, lang) {
  if (!iso) return "";
  const d = safeDate(iso);
  if (!d) return ""; // bad/empty input — render nothing rather than "NaN min ago"
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return lang === "it" ? "ora" : "just now";
  if (diffMin < 60) return lang === "it" ? `${diffMin} min fa` : `${diffMin} min ago`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return lang === "it" ? `${h}h fa` : `${h}h ago`;
  const days = Math.round(h / 24);
  return lang === "it" ? `${days}g fa` : `${days}d ago`;
}

// === Today / current-month helpers ===
function getTodayInfo(lang) {
  const now = new Date();
  const monthIdx = now.getMonth(); // 0=Jan
  const day = now.getDate();
  const dim = new Date(now.getFullYear(), monthIdx + 1, 0).getDate();
  const monthFraction = day / dim; // % of month elapsed
  const monthKey = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"][monthIdx];
  const labels = lang === "it"
    ? ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dateStr = `${day} ${labels[monthIdx]} ${now.getFullYear()}`;
  return { now, monthIdx, day, dim, monthFraction, monthKey, dateStr };
}

// === Editable inputs ===
// IMPORTANT: all hooks must be called BEFORE any conditional return (Rules of Hooks).
function EditableNumber({ value, onChange, suffix = "", prefix = "", min, max, className = "", allowEmpty = false, placeholder = "—", displayFormatter = null, kind = null, monthKey = null }) {
  const [editing, setEditing] = useState(false);
  const initial = (value == null || value === "") ? "" : String(value);
  const [tmp, setTmp] = useState(initial);
  const canEdit = useCanEdit(kind, monthKey);
  useEffect(() => { setTmp((value == null || value === "") ? "" : String(value)); }, [value]);
  const commit = () => {
    const trimmed = tmp.trim();
    if (allowEmpty && trimmed === "") { onChange(null); setEditing(false); return; }
    let n = parseFloat(trimmed.replace(",", "."));
    if (isNaN(n)) n = value;
    if (min != null && typeof n === "number") n = Math.max(min, n);
    if (max != null && typeof n === "number") n = Math.min(max, n);
    onChange(n);
    setEditing(false);
  };
  if (editing) {
    return (
      <input autoFocus type="text" inputMode="decimal" className={`edit-input ${className}`}
        value={tmp} onChange={(e) => setTmp(e.target.value)} onBlur={commit}
        placeholder={allowEmpty ? placeholder : ""}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        style={{ width: Math.max(54, (tmp.length + 1) * 9) + "px" }} />
    );
  }
  const isEmpty = value == null || value === "";
  return (
    <span className={`editable ${className} ${isEmpty ? "editable--empty" : ""} ${!canEdit ? "editable--locked" : ""}`}
      onClick={() => { if (canEdit) setEditing(true); }}
      title={canEdit ? "Click to edit · Clicca per modificare" : "🔒 Modifica bloccata · Editing locked"}>
      {isEmpty ? placeholder
        : displayFormatter ? displayFormatter(value)
        : <>{prefix}{typeof value === "number" ? (Math.round(value*100)/100).toLocaleString("de-DE") : value}{suffix}</>}
    </span>
  );
}

// EditableText moved to forecast-table.jsx (Phase B sub-PR 3)

// Compact stat shown in the sticky header when the user has scrolled past the KPI grid.
// Inline label · value · sub · pill, all on one line; appears only after 200px of vertical scroll.
// Reusable info icon — small "ⓘ" that reveals a CUSTOM-styled tooltip on hover (palette-
// matched, rounded, soft shadow). Replaces the ugly native browser title-attr tooltip.
// Place next to a title or label, never on values/numbers.
function InfoIcon({ tip, className = "" }) {
  if (!tip) return null;
  return (
    <span className={`info-wrap ${className}`}>
      <span className="info-icon" aria-label="info" tabIndex="0">ⓘ</span>
      <span className="info-tip" role="tooltip">{tip}</span>
    </span>
  );
}
// Same custom tooltip styling but as a wrapper — no icon, just hover anywhere on the wrapped
// element. Use for chips, badges, or any element where adding ⓘ would be noisy.
// Smart placement: defaults ABOVE; flips BELOW only if there's no room above (e.g. when the
// hovered element is near the top of the viewport, like the first row of a table).
function Tooltip({ tip, children, className = "" }) {
  if (!tip) return children;
  const [placement, setPlacement] = useState("above");
  const onEnter = (e) => {
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      // Approx tooltip height (worst case 6 lines of text @ ~16px line-height + padding)
      const TIP_H = 110;
      // Account for sticky header in the contracts table — if there's a sticky thead above,
      // its bottom edge is the actual top boundary for "above" tooltips.
      const stickyHeader = document.querySelector(".deals-table thead");
      const topBoundary = stickyHeader && rect.top < window.innerHeight
        ? Math.max(0, stickyHeader.getBoundingClientRect().bottom)
        : 0;
      setPlacement(rect.top - topBoundary < TIP_H ? "below" : "above");
    } catch { setPlacement("above"); }
  };
  return (
    <span className={`tip-wrap tip-wrap--${placement} ${className}`} onMouseEnter={onEnter} onFocus={onEnter}>
      {children}
      <span className="info-tip" role="tooltip">{tip}</span>
    </span>
  );
}

// PR 3 — sticky target chip restored on Marco's request. C2 had removed
// StickyHeaderStat because HeroKpi made it redundant at the top of the
// page; on scroll, though, HeroKpi disappears and there's no at-a-glance
// signal of "am I on track" anymore. This compact chip slots back into
// head__top's center slot when scrolled past 220px, showing only the
// target value + a status pill (simpler than the old StickyHeaderStat,
// which duplicated more of HeroKpi's content).
function StickyTargetChip({ visible, label, value, statusLabel, trackStatus }) {
  if (!visible) return null;
  return (
    <div className="sticky-target-chip" role="status">
      <span className="sticky-target-chip__label">{label}</span>
      <span className="sticky-target-chip__value">{value}</span>
      {statusLabel && (
        <span className={`status-pill status-pill--${trackStatus}`}>
          <span className="status-dot" />
          {statusLabel}
        </span>
      )}
    </div>
  );
}

// Floating scroll-to-top button — appears after scrolling 400px down.
function ScrollToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button className={`scroll-top ${show ? "show" : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top" title="Torna su · Scroll to top">↑</button>
  );
}

// Generic modal that replaces native prompt() / confirm().
// Variant: "confirm" (just yes/no), "prompt" (input + yes/no), "alert" (just OK).
function Modal({ open, onClose, title, message, variant = "confirm", confirmLabel = "OK", cancelLabel = "Annulla", danger = false, defaultValue = "", inputType = "text", error = null, onConfirm, onClearError }) {
  const [value, setValue] = useState(defaultValue);
  const dialogRef = useRef(null);
  useEffect(() => { if (open) setValue(defaultValue); }, [open, defaultValue]);
  // Escape parity with backdrop click — shared hook keeps behavior consistent across surfaces.
  useEscapeClose(open, onClose);
  // B5: focus trap + restore-on-close. Tab/Shift+Tab cycle inside; on close, focus
  // returns to the element that opened the modal.
  useFocusTrap(dialogRef, open);
  if (!open) return null;
  const submit = (e) => {
    e?.preventDefault();
    onConfirm(variant === "prompt" ? value : true);
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}
        ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={title ? "modal-title" : undefined}>
        {title && <h3 id="modal-title">{title}</h3>}
        {message && <p className="modal__msg">{message}</p>}
        {variant === "prompt" && (
          <form onSubmit={submit}>
            <input autoFocus type={inputType} className="modal__input"
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error && onClearError) onClearError(); }} />
          </form>
        )}
        {error && <div className="modal__err">{error}</div>}
        <div className="modal__actions">
          {variant !== "alert" && (
            <Button variant="ghost" size="md" onClick={onClose}>{cancelLabel}</Button>
          )}
          <Button variant={danger ? "danger" : "primary"} size="md"
            onClick={submit}
            disabled={variant === "prompt" && !value}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Mini-trend sparkline used in status cards. Shows last N values as a smooth line.
function Sparkline({ values, height = 26 }) {
  const valid = (values || []).filter(v => typeof v === "number" && !isNaN(v));
  if (valid.length < 2) return null;
  const w = 120, h = height;
  const min = Math.min(...valid), max = Math.max(...valid);
  const span = max - min || 1;
  const stepX = w / (valid.length - 1);
  const pts = valid.map((v, i) => [i * stepX, h - 2 - ((v - min) / span) * (h - 4)]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  const last = pts[pts.length - 1];
  return (
    <svg className="sparkline" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path className="sparkline__area" d={area} />
      <path className="sparkline__line" d={line} />
      <circle className="sparkline__dot" cx={last[0]} cy={last[1]} r={2.2} />
    </svg>
  );
}

function Kpi({ label, value, sub, accent }) {
  // accent supports legacy "true" (kpi--accent) and "cur" / "target" / etc named variants.
  const cls = accent === true ? "kpi kpi--accent" : accent ? `kpi kpi--${accent}` : "kpi";
  return (
    <div className={cls}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{value}</div>
      {sub && <div className="kpi__sub">{sub}</div>}
    </div>
  );
}

// Collapsible section header — chevron + title (clickable) + optional right-side controls
function SectionHeader({ title, open, onToggle, children, subtitle }) {
  return (
    <div className="card__head sec-head">
      <Tooltip tip={open ? "Comprimi · Collapse" : "Espandi · Expand"}>
        <button className="sec-head__title" onClick={onToggle}>
        <span className={`sec-chev ${open ? "sec-chev--open" : ""}`}>▸</span>
        <h3>{title}</h3>
        {subtitle && <span className="sec-head__sub">{subtitle}</span>}
        </button>
      </Tooltip>
      {open && children && <div className="sec-head__controls">{children}</div>}
    </div>
  );
}


// === Chart ===
// GrowthChart extracted to src/components/composed/GrowthChart.tsx (Phase 4 sub-step 3).
// Imported at top of file; JSX <GrowthChart> in Dashboard resolves to the composed module.

// Notification sound presets — Web Audio synth so we don't need any external file.
// Each preset is a function that takes an AudioContext and schedules oscillators.
const NOTIF_SOUND_PRESETS = {
  "ding-dong": {
    label: { it: "Ding-dong", en: "Ding-dong" },
    desc:  { it: "Classico bell a 2 toni", en: "Classic 2-tone chime" },
    play: (ctx, scale = 1) => {
      const t = (freq, at, dur = 0.18) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq;
        g.gain.value = 0;
        g.gain.linearRampToValueAtTime(0.08, ctx.currentTime + at + 0.02);
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + at + dur * scale);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + dur * scale);
      };
      t(659.25, 0); t(987.77, 0.13);
    },
  },
  "ka-ching": {
    label: { it: "Ka-ching", en: "Ka-ching" },
    desc:  { it: "Cassa registratrice (vibe 💰)", en: "Cash register vibe 💰" },
    play: (ctx, scale = 1) => {
      const now = ctx.currentTime;
      const bell = (freq, at, dur = 0.5, gain = 0.12) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "triangle"; o.frequency.value = freq;
        g.gain.value = 0;
        g.gain.linearRampToValueAtTime(gain, now + at + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, now + at + dur * scale);
        o.connect(g).connect(ctx.destination);
        o.start(now + at); o.stop(now + at + dur * scale);
      };
      bell(2000, 0, 0.3);
      bell(1500, 0.06, 0.35);
      bell(1000, 0.03, 0.55);
    },
  },
  "ka-ching-boost": {
    label: { it: "Ka-ching boost", en: "Ka-ching boost" },
    desc:  { it: "Cassa registratrice rinforzata + monetina che cade 🪙💰", en: "Stronger cash register + falling coin 🪙💰" },
    play: (ctx, scale = 1) => {
      const now = ctx.currentTime;
      const bell = (freq, at, dur, gain) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "triangle"; o.frequency.value = freq;
        g.gain.value = 0;
        g.gain.linearRampToValueAtTime(gain, now + at + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, now + at + dur * scale);
        o.connect(g).connect(ctx.destination);
        o.start(now + at); o.stop(now + at + dur * scale);
      };
      // initial sharp click (cash drawer opening)
      const click = (at) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "square"; o.frequency.value = 80;
        g.gain.value = 0;
        g.gain.linearRampToValueAtTime(0.18, now + at + 0.002);
        g.gain.exponentialRampToValueAtTime(0.001, now + at + 0.05);
        o.connect(g).connect(ctx.destination);
        o.start(now + at); o.stop(now + at + 0.05);
      };
      click(0);
      bell(2400, 0.04, 0.35, 0.18);
      bell(1800, 0.08, 0.4,  0.20);
      bell(1200, 0.05, 0.6,  0.18);
      bell(800,  0.10, 0.7,  0.14);
      // falling coin sweep at the end
      const sweep = ctx.createOscillator(), sg = ctx.createGain();
      sweep.type = "sine";
      sweep.frequency.setValueAtTime(2800, now + 0.45);
      sweep.frequency.exponentialRampToValueAtTime(900, now + 0.45 + 0.4 * scale);
      sg.gain.value = 0;
      sg.gain.linearRampToValueAtTime(0.10, now + 0.46);
      sg.gain.exponentialRampToValueAtTime(0.001, now + 0.45 + 0.45 * scale);
      sweep.connect(sg).connect(ctx.destination);
      sweep.start(now + 0.45); sweep.stop(now + 0.45 + 0.45 * scale);
    },
  },
  "bell-chime": {
    label: { it: "Carillon", en: "Bell chime" },
    desc:  { it: "Tre toni discendenti, dolce", en: "Three descending tones, gentle" },
    play: (ctx, scale = 1) => {
      const t = (freq, at, dur = 0.4) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq;
        g.gain.value = 0;
        g.gain.linearRampToValueAtTime(0.08, ctx.currentTime + at + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + dur * scale);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + dur * scale);
      };
      t(1046.5, 0, 0.45);
      t(880, 0.18, 0.45);
      t(659.25, 0.36, 0.55);
    },
  },
  "coin": {
    label: { it: "Moneta", en: "Coin" },
    desc:  { it: "Pingo acuto rapido (super-mario style)", en: "Quick high coin pop" },
    play: (ctx, scale = 1) => {
      const now = ctx.currentTime;
      const dur = 0.3 * scale;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(2200, now);
      o.frequency.exponentialRampToValueAtTime(1800, now + 0.18 * scale);
      g.gain.value = 0;
      g.gain.linearRampToValueAtTime(0.08, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now); o.stop(now + dur);
    },
  },
  "pop": {
    label: { it: "Pop", en: "Pop" },
    desc:  { it: "Piccolo pop morbido, minimale", en: "Subtle soft pop" },
    play: (ctx, scale = 1) => {
      const now = ctx.currentTime;
      const dur = 0.2 * scale;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(440, now);
      o.frequency.exponentialRampToValueAtTime(220, now + 0.15 * scale);
      g.gain.value = 0;
      g.gain.linearRampToValueAtTime(0.1, now + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now); o.stop(now + dur);
    },
  },
};
// Duration scale lets user stretch/shrink the notification sound. Stored separately.
const NOTIF_DURATIONS = {
  short:  { label: { it: "Corto", en: "Short" },   scale: 0.6 },
  normal: { label: { it: "Normale", en: "Normal" }, scale: 1.0 },
  long:   { label: { it: "Lungo", en: "Long" },   scale: 1.6 },
};
const playNotifSound = (key, durationKey = "normal") => {
  // Custom-recorded preset uses an HTMLAudioElement playing a base64 from localStorage.
  if (key === "custom") {
    try {
      const data = localStorage.getItem("forecast_notif_custom_audio");
      if (data) {
        const audio = new Audio(data);
        audio.volume = 0.8;
        audio.play().catch(() => {});
      }
    } catch (e) {}
    return;
  }
  const preset = NOTIF_SOUND_PRESETS[key];
  if (!preset) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const scale = (NOTIF_DURATIONS[durationKey] || NOTIF_DURATIONS.normal).scale;
    preset.play(ctx, scale);
    // Close the AudioContext after the longest preset (~1.5s) plus buffer. Without this,
    // every CW ping leaks an AudioContext — Chrome caps at 6 simultaneous, so the bell
    // silently stops working after ~6 plays in a session.
    setTimeout(() => { try { ctx.close(); } catch {} }, 2500);
  } catch (e) { /* autoplay blocked, ignore */ }
};

// Multi-select dropdown — shows "All" / "X selected" / single name as label,
// click opens a panel with checkboxes. Click outside closes.
// `value` is an array of selected ids. Empty array = "all".
function MultiSelectDropdown({ value, options, onChange, lang, allLabel, presetGroups }) {
  const [open, setOpen] = useState(false);
  // Keyboard parity with backdrop click — Escape closes the dropdown panel.
  useEscapeClose(open, () => setOpen(false));
  const selected = Array.isArray(value) ? value : [];
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  const labelText = (() => {
    if (selected.length === 0) return allLabel || (lang === "it" ? "Tutti" : "All");
    if (selected.length === 1) {
      const o = options.find(x => x.id === selected[0]);
      return o?.label || selected[0];
    }
    return `${selected.length} ${lang === "it" ? "selez." : "selected"}`;
  })();
  return (
    <div className="multiselect">
      <button className={`multiselect__btn ${selected.length > 0 ? "on" : ""}`}
        onClick={() => setOpen(o => !o)}>
        {labelText} <span className="multiselect__chev">▾</span>
      </button>
      {open && (
        <>
          <div className="multiselect__backdrop" onClick={() => setOpen(false)} />
          <div className="multiselect__panel">
            <button className="multiselect__item multiselect__item--reset"
              onClick={() => { onChange([]); setOpen(false); }}>
              <span className="multiselect__check">{selected.length === 0 ? "●" : "○"}</span>
              {allLabel || (lang === "it" ? "Tutti" : "All")}
            </button>
            {presetGroups && presetGroups.map(g => (
              <button key={g.id} className="multiselect__item multiselect__item--preset"
                onClick={() => onChange(g.ids)}>
                <span className="multiselect__check">○</span>
                {g.label}
              </button>
            ))}
            {(presetGroups || options.length > 0) && <div className="multiselect__sep" />}
            <div className="multiselect__list">
              {options.map(o => (
                <label key={o.id} className="multiselect__item">
                  <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// SoundPickerModal — moved to inputs-panel.jsx (Phase B sub-PR 5).
// NOTIF_SOUND_PRESETS, NOTIF_DURATIONS, and playNotifSound stay here because
// they're also used by the notification bell + settings UI farther down.
// They're exposed on window so SoundPickerModal can read them at render time.
Object.assign(window, { NOTIF_SOUND_PRESETS, NOTIF_DURATIONS, playNotifSound });

// === Notification bell ===
// Reads recent Closed Won / Closed Lost deals (last 30 days) from `actuals.deals`,
// keeps a "seen" set in localStorage so unread items show a dot. Click bell → hover panel.
// NotificationBell extracted to src/components/composed/NotificationBell.tsx
// (Phase 4 sub-step 8). Imported at top; JSX <NotificationBell> resolves there.
function MonthFocusChart({ row, lang, height }) {
  if (!row) return null;
  const w = 1440, h = height || 360;
  const channels = [
    { k: "gtm",     label: "GTM",        target: row.gtmTarget,    actual: row.gtmActual,    color: "var(--lilac-700)" },
    { k: "ae",      label: "AE",         target: row.aeChannelGap, actual: null,             color: "var(--ae-500)", computed: true },
    { k: "partner", label: "Partner",    target: row.partnerMrr,   actual: row.partnerActual,color: "var(--lilac-400)" },
    { k: "self",    label: "Self-Serve", target: row.selfServeMrr, actual: row.selfServeActual,color: "var(--lilac-300)" },
    { k: "others",  label: "Others",     target: 0,                actual: row.othersTotal,  color: "var(--lilac-200)", noTarget: true },
    { k: "churn",   label: "Churn",      target: row.churnAbs,     actual: null,             color: "var(--danger-soft)", isChurn: true },
  ];
  const maxV = Math.max(1, ...channels.map(c => Math.max(c.target || 0, c.actual || 0)));
  const niceCeil = (v) => {
    if (v <= 0) return 1000;
    const exp = Math.floor(Math.log10(v));
    const pow = Math.pow(10, exp);
    const norm = v / pow;
    const stops = [1, 1.2, 1.25, 1.5, 1.6, 1.8, 2, 2.2, 2.5, 2.8, 3, 3.2, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 10];
    let nice = 10;
    for (const s of stops) { if (norm <= s) { nice = s; break; } }
    return nice * pow;
  };
  const niceMax = niceCeil(maxV * 1.05);
  const pad = { l: 130, r: 200, t: 28, b: 36 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const rowH = innerH / channels.length;
  const xScale = (v) => (v / niceMax) * innerW;
  const fmtTick = (v) => window.fmtMoney ? window.fmtMoney(v) : ("€" + Math.round(v));
  const ticks = []; for (let i = 0; i <= 4; i++) ticks.push((niceMax * i) / 4);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart" preserveAspectRatio="xMidYMid meet">
      {/* x-grid */}
      {ticks.map((tv, i) => (
        <g key={i}>
          <line x1={pad.l + xScale(tv)} x2={pad.l + xScale(tv)} y1={pad.t} y2={pad.t + innerH}
            stroke="var(--line)" strokeDasharray="2 4" />
          <text x={pad.l + xScale(tv)} y={pad.t + innerH + 22} textAnchor="middle" className="chart__tick">{fmtTick(tv)}</text>
        </g>
      ))}
      {channels.map((c, i) => {
        const cy = pad.t + i * rowH + rowH / 2;
        const tBarH = 16, aBarH = 22;
        const tW = c.target > 0 ? xScale(c.target) : 0;
        const aW = (c.actual != null && c.actual > 0) ? xScale(c.actual) : 0;
        const pct = (c.actual != null && c.target > 0) ? (c.actual / c.target) * 100 : null;
        const tone = pct == null ? "neutral" : pct >= 95 ? "good" : pct >= 70 ? "ok" : "bad";
        const badgeColor = tone === "good" ? "var(--good)" : tone === "ok" ? "var(--warn-mid)" : tone === "bad" ? "var(--warn)" : "var(--lilac-500)";
        return (
          <g key={c.k}>
            {/* label */}
            <text x={pad.l - 16} y={cy + 5} textAnchor="end" fontSize={window.TOKENS.fs.h3} fontWeight="600" fill="var(--ink-1)" fontFamily="Geist, sans-serif">{c.label}</text>
            {/* target bar (lighter) */}
            {!c.noTarget && (
              <rect x={pad.l} y={cy - tBarH - 2} width={Math.max(2, tW)} height={tBarH} fill={c.color} opacity="0.32" rx="3" />
            )}
            {/* actual bar (solid) — for churn we just show target */}
            <rect x={pad.l} y={cy + 2} width={Math.max(2, c.isChurn ? tW : aW)} height={aBarH} fill={c.color} opacity={c.computed || c.isChurn || c.noTarget ? 0.7 : 1} rx="3" />
            {/* values to the right */}
            <text x={pad.l + Math.max(tW, aW) + 12} y={cy - 2} fontSize={window.TOKENS.fs.body} fontWeight="500" fill="var(--ink-3)" fontFamily="Geist Mono, monospace">
              {c.noTarget ? "—" : `T ${window.fmtMoney(c.target)}`}
            </text>
            <text x={pad.l + Math.max(tW, aW) + 12} y={cy + 18} fontSize={window.TOKENS.fs["body-lg"]} fontWeight="700" fill="var(--ink-1)" fontFamily="Geist Mono, monospace">
              {c.isChurn ? `${window.fmtMoney(c.target)}` :
                c.computed ? `${window.fmtMoney(c.target)}` :
                (c.actual != null ? `R ${window.fmtMoney(c.actual)}` : "R —")}
            </text>
            {/* perf pill */}
            {pct != null && (
              <g>
                <rect x={pad.l + Math.max(tW, aW) + 130} y={cy - 8} width={48} height={20} rx={10} fill={badgeColor} />
                <text x={pad.l + Math.max(tW, aW) + 154} y={cy + 6} textAnchor="middle" fontSize={window.TOKENS.fs["body-sm"]} fontWeight="700" fill="white" fontFamily="Geist Mono, monospace">{Math.round(pct)}%</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// === Quarter focused chart: grouped bars (target vs real) per month per channel — handled by GrowthChart with barMode=grouped ===

// === Dashboard ===
function Dashboard() {
  // Phase 5.4: pull viewer state from the provider wired in main.tsx.
  // Used downstream for the picker gate + the X-User-Id header (5.6).
  const { viewer: __viewer, loading: __viewerLoading, refresh: __refreshViewer } = useViewer();

  // (Phase C2: headScrolled state + scroll listener removed alongside
  // StickyHeaderStat. The head--scrolled CSS hook is also gone — its only
  // purpose was to collapse the brand row while the sticky stat occupied
  // the center column; with the stat removed there's nothing to make
  // room for, and the brand row stays visible at all scroll positions.)

  // STORAGE_KEY: kept as a Dashboard-scope constant because the "reset all"
  // handler farther down also references it. The localStorage + KV save/load
  // logic moved to state-storage.jsx::useStateStorage (called below, after
  // the auth state is declared).
  const STORAGE_KEY = "fonio_forecast_v9";

  // UI-only state
  const [chartExpanded] = useState(true);
  const [tableExpanded, setTableExpanded] = useState(true);
  // Independent timeframe for the Contratti section (does NOT mirror header timeframe per Marco's spec).
  const [dealsTimeframe, setDealsTimeframe] = useState({ mode: "fy" });
  const [inputDataOpen, setInputDataOpen] = useState(false);
  const [inputsPanelOpen, setInputsPanelOpen] = useState(false);
  // Inputs panel: Escape closes (backdrop click already handled by the rendered overlay)
  useEscapeClose(inputsPanelOpen, () => setInputsPanelOpen(false));
  const [timeframe, setTimeframe] = useState(() => ({ mode: "fy" })); // {mode, monthKey?, qtrIdx?, customStart?, customEnd?}
  const [tfMenuOpen, setTfMenuOpen] = useState(null); // null | "month" | "qtr" | "custom"
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [logScale, setLogScale] = useState(false);
  const [barMode, setBarMode] = useState("stacked"); // "stacked" | "grouped"
  const [valueMode, setValueMode] = useState("mrr"); // "mrr" | "arr"  (global)
  const [tableValueMode, setTableValueMode] = useState("mrr"); // table is independent: "mrr" | "arr"
  const [aeChartValueMode, setAeChartValueMode] = useState("mrr"); // AE chart is independent: "mrr" | "arr"
  const [yAxisLocked, setYAxisLocked] = useState(true); // when true, use full FY max even if zoomed
  // PR 3 — sticky-target visibility on scroll (restored from C2's removal).
  // Toggles past 220px scroll, drives the at-a-glance chip in head__top.
  const [scrolledPast, setScrolledPast] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolledPast(window.scrollY > 220);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Phase B2 — theme moved to lib/theme.ts. The hook owns first-load
  // resolution (localStorage > prefers-color-scheme > "light"), DOM
  // application, persistence, and cross-tab sync. Subscribing here means
  // ForecastItaly re-renders when the topbar toggles theme too.
  const [theme, setTheme] = useTheme();
  // PR 1 — design preset, drives the platform's overall visual style
  // (button shape, density, shadow, accent). Switcher lives in the
  // settings dropdown below; both theme + design are persisted
  // independently and orthogonally.
  const [design, setDesignPref] = useDesign();
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Notification sound preferences (persisted in localStorage). "off" = mute.
  const [notifSound, setNotifSound] = useState(() => {
    try { return localStorage.getItem("forecast_notif_sound") || "ding-dong"; } catch { return "ding-dong"; }
  });
  const setNotifSoundSaved = (val) => {
    setNotifSound(val);
    try { localStorage.setItem("forecast_notif_sound", val); } catch {}
  };
  const [notifDuration, setNotifDuration] = useState(() => {
    try { return localStorage.getItem("forecast_notif_duration") || "normal"; } catch { return "normal"; }
  });
  const setNotifDurationSaved = (val) => {
    setNotifDuration(val);
    try { localStorage.setItem("forecast_notif_duration", val); } catch {}
  };
  // Track whether a custom recording exists in localStorage (lets us show the preview button)
  const [hasCustomAudio, setHasCustomAudio] = useState(() => {
    try { return !!localStorage.getItem("forecast_notif_custom_audio"); } catch { return false; }
  });

  // ─── HubSpot integration + auth ───────────────────────────────────────────
  // VIEW password unlocks data load. EDIT password unlocks editing + state writes.
  const [viewPassword, setViewPassword] = useState(() => {
    try { return localStorage.getItem("forecast_view_pw") || ""; } catch (e) { return ""; }
  });
  const [editMode, setEditMode] = useState(false);
  const [editPassword, setEditPassword] = useState(""); // kept in memory only, never persisted
  const [actuals, setActuals] = useState(null); // {months: {feb: {gtmActual,...}}, fetchedAt, fromCache}
  const [actualsLoading, setActualsLoading] = useState(false);
  const actualsLoadingRef = React.useRef(false);
  // Track unmount so in-flight fetches don't write to setState after unmount
  // (happens when ErrorBoundary catches a crash + re-mounts — old fetch lands and
  // overwrites the recovered state). All fetchActuals setters check this ref.
  const mountedRef = React.useRef(true);
  React.useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const [actualsLoadingProgress, setActualsLoadingProgress] = useState(null); // {done, total} during refresh
  const [actualsError, setActualsError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [hasInitialActuals, setHasInitialActuals] = useState(false);

  const hubspotEnabled = !!WORKER_URL;

  // ─── Persistence layer (Phase B sub-PR 1) ───────────────────────────────
  // useStateStorage owns localStorage + KV load/save and runs Phase A boundary
  // validation at every load point. Returns the live state + setter + the two
  // saved-at timestamps used by the header sync indicator.
  const { state, setState, savedAt, kvSavedAt } = window.useStateStorage({
    storageKey: STORAGE_KEY,
    defaultState: window.DEFAULT_STATE,
    hubspotEnabled,
    viewPassword,
    editMode,
    editPassword,
    workerUrl: WORKER_URL,
  });
  // Phase 4e — broadcast lang changes to admin views + primitives. Fires
  // once on mount + every time state.lang flips via the IT/EN toggle.
  useEffect(() => { publishLang(state.lang); }, [state.lang]);
  // Phase B3 — listen for external lang changes (e.g. from AppTopbar's
  // IT/EN toggle on a non-forecast route). When the broadcast lang
  // disagrees with the in-memory state.lang, patch state so the local
  // tree stays consistent with the persisted value. The guard against
  // equal values prevents the obvious cycle (publish → subscribe →
  // setState → publishLang ...). setState is called directly (not via
  // the local setTop wrapper) because setTop is declared later in this
  // function — at runtime the effect fires after both are in scope, but
  // forward-referencing const inside the useEffect callback would land
  // in the TDZ if React ever ran effects synchronously during render.
  useEffect(() => {
    const onLang = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if ((detail === "it" || detail === "en") && detail !== state.lang) {
        setState(s => ({ ...s, lang: detail }));
      }
    };
    window.addEventListener("forecast-lang-change", onLang as EventListener);
    return () => window.removeEventListener("forecast-lang-change", onLang as EventListener);
  }, [state.lang]);
  const ACTUAL_CELLS = ["gtmActual", "partnerActual", "selfServeActual", "setupUpsell", "upsellPipeline", "churnAbs", "gtmCommitted"];
  const fetchActuals = async (forceRefresh = false) => {
    if (!hubspotEnabled || !viewPassword) return;
    if (actualsLoadingRef.current) return; // already in flight — prevent overlap
    actualsLoadingRef.current = true;
    setActualsLoading(true); setActualsError(null);
    // Guarded setter helpers — bail silently if the component has unmounted (e.g. after
    // ErrorBoundary recovery). Without these, an in-flight HubSpot fetch would call
    // setActuals on a dead tree and either warn (dev) or stomp the recovered state (prod).
    const safeSetActuals  = (v) => { if (mountedRef.current) setActuals(v); };
    const safeSetError    = (v) => { if (mountedRef.current) setActualsError(v); };
    const safeSetProgress = (v) => { if (mountedRef.current) setActualsLoadingProgress(v); };
    const safeSetAuth     = (v) => { if (mountedRef.current) setAuthError(v); };
    const safeSetViewPw   = (v) => { if (mountedRef.current) setViewPassword(v); };
    const safeSetHasInit  = (v) => { if (mountedRef.current) setHasInitialActuals(v); };
    try {
      const headers = { "X-View-Password": viewPassword };
      // Phase 7.5c — append the active market slug. window.MARKET_CONFIG is
      // set by MarketDashboard before render; falls back to italy for the
      // legacy /forecast/italy route.
      const marketSlug = (window as any).MARKET_CONFIG?.slug || "italy";
      const marketQs = `market=${encodeURIComponent(marketSlug)}`;
      if (forceRefresh) {
        // Refresh each cell in parallel — track per-cell completion for the progress UI.
        let done = 0;
        safeSetProgress({ done: 0, total: ACTUAL_CELLS.length });
        const responses = await Promise.all(ACTUAL_CELLS.map(cell =>
          fetch(`${WORKER_URL}/api/actuals?cell=${cell}&refresh=1&${marketQs}`, { headers })
            .then(r => { done += 1; safeSetProgress({ done, total: ACTUAL_CELLS.length }); return r; })
        ));
        if (!mountedRef.current) return;
        if (responses.some(r => r.status === 401)) {
          safeSetAuth("view_invalid"); safeSetViewPw(""); localStorage.removeItem("forecast_view_pw"); return;
        }
        const mergedResp = await fetch(`${WORKER_URL}/api/actuals?${marketQs}`, { headers });
        if (!mountedRef.current) return;
        if (!mergedResp.ok) { safeSetError(`HTTP ${mergedResp.status}`); return; }
        // Phase A: validate /api/actuals shape before storing. Malformed
        // payloads (worker bug, partial deploy) are dropped + toasted; the
        // hydrate effect (line ~3000) is a no-op when actuals is null.
        const mergedJson = await mergedResp.json();
        const validatedMerged = validateOrFallback(
          window.SCHEMAS && window.SCHEMAS.HubSpotActualsResponseSchema,
          mergedJson, null, "hubspotActuals", state.lang
        );
        if (validatedMerged) safeSetActuals(validatedMerged);
      } else {
        const resp = await fetch(`${WORKER_URL}/api/actuals?${marketQs}`, { headers });
        if (!mountedRef.current) return;
        if (resp.status === 401) { safeSetAuth("view_invalid"); safeSetViewPw(""); localStorage.removeItem("forecast_view_pw"); return; }
        if (!resp.ok) { safeSetError(`HTTP ${resp.status}`); return; }
        const respJson = await resp.json();
        const validatedResp = validateOrFallback(
          window.SCHEMAS && window.SCHEMAS.HubSpotActualsResponseSchema,
          respJson, null, "hubspotActuals", state.lang
        );
        if (validatedResp) safeSetActuals(validatedResp);
      }
      safeSetHasInit(true);
      // Successful auth — clear any password-strike state.
      try { localStorage.removeItem("forecast_pw_strikes"); localStorage.removeItem("forecast_pw_cooldown"); } catch {}
    } catch (e) {
      safeSetError(e.message || String(e));
    } finally {
      actualsLoadingRef.current = false;
      if (mountedRef.current) {
        setActualsLoading(false);
        setActualsLoadingProgress(null);
      }
    }
  };
  // Hydrate state.months[k] with HubSpot actuals whenever they change.
  // Note for churn: we mirror HubSpot's value into BOTH `churnAbs` (planned/displayed) AND
  // `churnActual` (HubSpot real). The realEom calc uses churnActual to distinguish "real
  // happened" from "planned forecast" — for non-locked months without HubSpot churn, churnActual
  // stays null and realEom subtracts 0 (per Marco's option (a)).
  //
  // ─── Stale-actuals fix (May 2026, "June ghost deal" episode) ────────────
  // Hydration is CLEAR-then-apply, not additive. A previous fetch had bucketed
  // some deal (likely the original Fornovo, before Marco recreated it with a
  // May close-date) into June, writing €1.014 to state.months.jun.gtmActual.
  // After the deal moved to May, the Worker stopped returning a June value
  // — but the local state still held €1.014 forever, because the old code
  // only wrote when `typeof vals[f] === "number"` and never reset.
  // Now: every hydrate, we first null-out the HubSpot-managed *Actual fields
  // for every month in our forecast, then apply whatever HubSpot's response
  // actually has. `actuals` is only set on a successful fetch (see
  // fetchActuals — failures are no-ops on the actuals state), so a transient
  // network error can't blank the dashboard.
  // churnAbs is NOT reset because it doubles as the user's forecast for
  // future months; only churnActual (the pure HubSpot mirror) is cleared.
  useEffect(() => {
    if (!actuals?.months) return;
    // Stability guard (added 2026-05 alongside the revert of PR #46-48):
    // The clear-then-apply pattern below blanks the *Actual fields for
    // every month before re-applying values from this fetch. That's the
    // right behaviour when the response is authoritative — but if the
    // worker returns an empty months map (e.g. cold cache right after a
    // redeploy, or every cell missing from KV), we would zero the whole
    // dashboard. Guard against that: only proceed if the response
    // actually carries at least one numeric channel value. An empty
    // payload means "no data yet" — keep the existing state untouched
    // so the user sees the previous numbers until the next fetch lands.
    const hasAnyNumeric = Object.values(actuals.months).some(monthVals => {
      if (!monthVals || typeof monthVals !== "object") return false;
      for (const v of Object.values(monthVals)) {
        if (typeof v === "number" && !Number.isNaN(v)) return true;
      }
      return false;
    });
    if (!hasAnyNumeric) return;
    setState(s => {
      const newMonths = { ...s.months };
      // Pure-passthrough fields: HubSpot value → state field of same name.
      // otherUpsellActual was added by PR #43 alongside the worker's
      // upsellPipeline crossCheck — captures non-team-owned upsells that
      // no longer roll into upsellPipelineActual. Kept in the clear list
      // so an inflight worker that returns the new field gets cleaned
      // properly; missing field on older worker responses is a no-op.
      const fields = ["gtmActual", "partnerActual", "selfServeActual", "otherActual", "otherUpsellActual"];
      // Forecast→Actual remap: the worker still returns `setupUpsell` / `upsellPipeline`
      // (cell names match the deal-tag names) but on the client we write them to
      // *Actual fields so the editable forecast in setupUpsell / upsellPipeline is preserved.
      const remap = { setupUpsell: "setupUpsellActual", upsellPipeline: "upsellPipelineActual" };
      const remapDsts = Object.values(remap); // ["setupUpsellActual", "upsellPipelineActual"]
      // Step 1: clear HubSpot-managed *Actual fields across every forecast
      // month so values from a previous fetch don't survive into the new one.
      for (const mKey of Object.keys(newMonths)) {
        const updated = { ...newMonths[mKey] };
        for (const f of fields) updated[f] = null;
        for (const dst of remapDsts) updated[dst] = null;
        updated.churnActual = null;
        newMonths[mKey] = updated;
      }
      // Step 2: apply this fetch's actuals over the cleared baseline.
      for (const [mKey, vals] of Object.entries(actuals.months)) {
        if (!newMonths[mKey]) continue; // ignore months not in our forecast (jan)
        const updated = { ...newMonths[mKey] };
        for (const f of fields) {
          if (typeof vals[f] === "number") updated[f] = Math.round(vals[f]);
        }
        for (const [src, dst] of Object.entries(remap)) {
          if (typeof vals[src] === "number") updated[dst] = Math.round(vals[src]);
        }
        if (typeof vals.churnAbs === "number") {
          updated.churnAbs = Math.round(vals.churnAbs);
          updated.churnActual = updated.churnAbs;
        }
        newMonths[mKey] = updated;
      }
      return { ...s, months: newMonths };
    });
  }, [actuals]);
  // Initial fetch on mount + when viewPassword set
  useEffect(() => { if (viewPassword) fetchActuals(false); }, [viewPassword]);
  // Auto-refresh from HubSpot every 15 minutes (only when the tab is visible — no point
  // burning Worker subreqs while the user is on another tab). Soft refresh: fetches each
  // cell with refresh=1 so new Closed Wons show up + the bell can ping accordingly.
  useEffect(() => {
    if (!viewPassword || !hubspotEnabled) return;
    const POLL_MS = 15 * 60 * 1000;
    const tick = () => {
      // Re-entry guards:
      //  - skip if a manual or previous-tick refresh is still in flight (ref → no stale closure)
      //  - skip if tab is hidden — no point burning Worker subreqs while user is elsewhere
      if (actualsLoadingRef.current) return;
      if (document.visibilityState !== "visible") return;
      fetchActuals(true);
    };
    const interval = setInterval(tick, POLL_MS);
    return () => clearInterval(interval);
  }, [viewPassword, hubspotEnabled]);

  // Section open/closed state — every major section is collapsible
  const [sections, setSections] = useState({
    chart: true,
    aeChart: true,
    inputs: false,
    table: true,
    deals: true,
    notes: true,
  });
  const toggleSection = (k) => setSections(s => ({ ...s, [k]: !s[k] }));

  // Local ARR-aware formatters for the dashboard header + KPIs.
  // We DO NOT rebind window.eur/window.fmtMoney — composing global wrappers across renders
  // caused the ×144 stacking bug. Instead, every call site in this Dashboard scope uses these
  // locals, and subcomponents that should stay MRR (table rows, channel detail) keep using
  // the pure window.__FMT_MONEY_MRR_PURE via the `mrrFmt` prop.
  const arrMul = valueMode === "arr" ? 12 : 1;
  const globalEur = (n) => window.__EUR_MRR_PURE((n || 0) * arrMul);
  const globalFmt = (n, opts) => window.__FMT_MONEY_MRR_PURE((n || 0) * arrMul, opts);
  // Label helper: when in ARR mode, swap "MRR" → "ARR" in any string (KPI labels, sub-labels, ecc).
  const lbl = (s) => valueMode === "arr" ? String(s).replace(/MRR/g, "ARR") : s;
  // Keep these aliases pointing at the immutable pures so the GrowthChart's local
  // tick formatter (which reads window.__fmtMoneyMrr) works without a global rebind.
  if (typeof window !== "undefined") {
    window.__eurMrr = window.__EUR_MRR_PURE;
    window.__fmtMoneyMrr = window.__FMT_MONEY_MRR_PURE;
  }
  // Always-MRR formatter (ignores ARR toggle) — used in the table and AE chart for cells that
  // should never annualize (per-AE actuals, channel mix, churn, SDR).
  const mrrFmt = window.__FMT_MONEY_MRR_PURE;
  // Table-local ARR-aware formatter — used for Tot MRR EOM and Target cells.
  // Defaults to MRR (so the table is purely MRR by default); flips only when user clicks ARR
  // on the table's own toggle. Independent of the global toggle.
  const tableArrMul = (tableValueMode === "arr") ? 12 : 1;
  const tableEur = (n) => mrrFmt((n || 0) * tableArrMul);

  // Auto-switch bar mode based on timeframe (gentle hint, user can override)
  useEffect(() => {
    if (timeframe.mode === "qtr") setBarMode("grouped");
    else if (timeframe.mode === "fy" || timeframe.mode === "ytd") setBarMode("stacked");
  }, [timeframe.mode]);

  // Persistence (Phase B sub-PR 1) — moved to state-storage.jsx::useStateStorage.
  // The hook owns: localStorage save (debounced 200ms, COR-2 throttled toast),
  // KV /api/state load (once on mount with view password), KV /api/state save
  // (debounced 1500ms, full-edit or ae-scope, race-protected via seq counter).
  //
  // Hook call lives where useStateStorage is wired in — see the top of
  // Dashboard, just after the auth-state declarations.

  // Browser tab title — current month + last closed real for at-a-glance status.
  useEffect(() => {
    const lastClosed = [...(window.computeForecast(state) || [])].reverse().find(r => r.locked);
    const mul = valueMode === "arr" ? 12 : 1;
    const valStr = lastClosed?.realEom != null ? window.__FMT_MONEY_MRR_PURE(lastClosed.realEom * mul) : "—";
    const monthStr = state.lang === "it"
      ? ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][new Date().getMonth()]
      : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][new Date().getMonth()];
    const unitStr = valueMode === "arr" ? "ARR" : "MRR";
    document.title = `${monthStr} · ${valStr} ${unitStr} · fonio Forecast`;
  }, [state, valueMode]);

  // ─── Modal state (replaces native prompt/confirm) ───
  // Generic dispatcher: setModal({...}) opens, setModal(null) closes.
  const [modal, setModal] = useState(null);

  const today = useMemo(() => getTodayInfo(state.lang), [state.lang]);
  const t = window.STRINGS[state.lang];
  // Scenario scale: when an active non-base scenario is selected, scale all non-locked
  // future-month targets proportionally (scenario.eomTarget / base Dec eomTarget).
  const activeScenario = state.scenarios?.active || "base";
  const scenarioScale = useMemo(() => {
    if (activeScenario === "base") return 1;
    const sc = state.scenarios?.[activeScenario];
    const baseEoy = state.months?.dec?.eomTarget;
    if (!sc?.eomTarget || !baseEoy) return 1;
    return sc.eomTarget / baseEoy;
  }, [activeScenario, state.scenarios, state.months?.dec?.eomTarget]);
  // Two-pass forecast: first pass without dynamicArpu (so we can compute economics from the
   // base scenario), then recompute future months with that ARPU substituted for avgBasket.
   // Past months are locked → unaffected. Result: SDR/AE math on future months reflects real ticket size.
  const rowsBasePass = useMemo(() => window.computeForecast(state, scenarioScale), [state, scenarioScale]);
  // Channel filter for ARPU/LTV: which deal cells count as "customers". Default in
  // DEFAULT_STATE is ["gtm","selfServe","upsells"] — Partner + Other excluded, Upsells
  // fold into revenue but not the count. Translates user-facing keys into worker `cell`
  // tags. "upsells" is special: numerator-only (added to revenue, not to customer count).
  const ARPU_CHANNEL_MAP = {
    gtm:       ["gtmActual"],
    selfServe: ["selfServeActual"],
    partner:   ["partnerActual"],
    other:     ["otherActual"],                  // non-team Italy deals (e.g. Christopher's)
    upsells:   ["setupUpsell", "upsellPipeline"], // numerator-only — handled separately
  };
  const arpuChannels = Array.isArray(state.arpuChannels) ? state.arpuChannels : ["gtm", "selfServe", "upsells"];
  // Cells that produce CUSTOMERS (count + base revenue): everything except upsells.
  const customerCells = arpuChannels
    .filter(k => k !== "upsells")
    .flatMap(k => ARPU_CHANNEL_MAP[k] || []);
  const includeUpsells = arpuChannels.includes("upsells");

  // Match key for linking an upsell deal to its base customer. We don't have a
  // HubSpot company association exposed, so we fall back to a normalized
  // `company || name`. Approximate but consistent with how the rest of the
  // codebase identifies customers across pipelines.
  const customerKey = (d) => (d?.company || d?.name || "").trim().toLowerCase();

  // Live ARPU computed from HubSpot deals. Filters by selected customer-channels.
  // Upsells are added to the numerator ONLY for customers whose base deal is also
  // in customerCells — so toggling channels in the dropdown moves both pieces
  // together (no orphan upsell revenue inflating ARPU when its base channel is off).
  const dynamicArpu = useMemo(() => {
    const allDeals = actuals?.deals || [];
    const customerDeals = allDeals.filter(d =>
      STAGE_LABELS[d.stage]?.isClosedWon
      && customerCells.includes(d.cell)
      && d.closeDate && new Date(d.closeDate).getTime() <= Date.now()
    );
    const churnedDeals = allDeals.filter(d =>
      STAGE_LABELS[d.stage]?.isClosedLost && customerCells.includes(d.cell)
    );
    const customerCount = Math.max(1, customerDeals.length - churnedDeals.length);
    const baseRevenue  = customerDeals.reduce((s, d) => s + (d.amount || 0), 0);
    const churnRevenue = churnedDeals.reduce((s, d) => s + (d.amount || 0), 0);
    const customerKeys = new Set(customerDeals.map(customerKey).filter(Boolean));
    const upsellRevenue = includeUpsells
      ? allDeals
          .filter(d =>
            STAGE_LABELS[d.stage]?.isClosedWon
            && (d.cell === "setupUpsell" || d.cell === "upsellPipeline")
            && customerKeys.has(customerKey(d))
          )
          .reduce((s, d) => s + (d.amount || 0), 0)
      : 0;
    const revenue = baseRevenue - churnRevenue + upsellRevenue;
    if (customerDeals.length === 0 || revenue <= 0) return null;
    return revenue / customerCount;
  }, [actuals?.deals, customerCells.join(","), includeUpsells]);
  // Effective basket override fed to the model: null when auto=on (model uses dynamicArpu),
  // or the live dynamic value when manual override is off. When user has clicked "Forza",
  // state.avgBasketAuto becomes false and the model uses state.avgBasket directly (no override).
  const basketOverride = (state.avgBasketAuto !== false && dynamicArpu) ? dynamicArpu : null;
  const rows = useMemo(() => window.computeForecast(state, scenarioScale, basketOverride), [state, scenarioScale, basketOverride]);
  // Pre-computed rows for the OTHER two scenarios — used as comparison lines on the chart.
  const baseEoy_ = state.months?.dec?.eomTarget || 0;
  const compareScenarios = useMemo(() => {
    const optScale = (state.scenarios?.optimistic?.eomTarget && baseEoy_) ? state.scenarios.optimistic.eomTarget / baseEoy_ : 1.2;
    const pessScale = (state.scenarios?.pessimistic?.eomTarget && baseEoy_) ? state.scenarios.pessimistic.eomTarget / baseEoy_ : 0.7;
    return {
      optimistic: window.computeForecast(state, optScale),
      pessimistic: window.computeForecast(state, pessScale),
      base: window.computeForecast(state, 1),
    };
  }, [state, baseEoy_]);
  // Chart scenario visibility toggles — defaults to ["base"]; empty array means "show all 3" (compare mode).
  const chartScenarios = state.chartScenarios || ["base"];
  const toggleChartScn = (key) => setState(s => {
    const cur = s.chartScenarios || [];
    return { ...s, chartScenarios: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
  });
  // Y-axis lock max: include max across ALL scenarios so the axis stays fixed when toggling.
  const allScenariosMax = useMemo(() => {
    const vals = [
      ...rows.map(r => Math.max(r.realEom || 0, r.eomTarget || 0)),
      ...((compareScenarios.optimistic || []).map(r => r.eomTarget || 0)),
      ...((compareScenarios.pessimistic || []).map(r => r.eomTarget || 0)),
    ];
    return Math.max(0, ...vals);
  }, [rows, compareScenarios]);
  const lastClosed = [...rows].reverse().find(r => r.locked);
  const decRow = rows.find(r => r.key === "dec");
  const targetMrr = decRow ? decRow.eomTarget : 250000;
  const currentMrr = lastClosed ? lastClosed.realEom : 0;
  const gap = Math.max(0, targetMrr - currentMrr);
  const monthsLeft = rows.filter(r => !r.locked).length;
  const avgGrowth = monthsLeft > 0 ? gap / monthsLeft : 0;

  // Where SHOULD we be today? Linear-progress through plan: prevMonthEomTarget + monthFraction*(curMonthEomTarget - prevEom)
  const todayMonthIdxInRows = window.MONTH_KEYS.indexOf(today.monthKey);
  let expectedNow = currentMrr;
  if (todayMonthIdxInRows >= 0 && todayMonthIdxInRows < rows.length) {
    const r = rows[todayMonthIdxInRows];
    const prevR = todayMonthIdxInRows > 0 ? rows[todayMonthIdxInRows - 1] : null;
    const prevEom = prevR ? prevR.eomTarget : 0;
    expectedNow = prevEom + (r.eomTarget - prevEom) * today.monthFraction;
  }
  const aheadDelta = currentMrr - expectedNow;
  const aheadPct = expectedNow > 0 ? (aheadDelta / expectedNow) * 100 : 0;
  const trackStatus = Math.abs(aheadPct) < 3 ? "on" : aheadDelta > 0 ? "ahead" : "behind";

  // ARPU + LTV — derived from HubSpot deal records, filtered by state.arpuChannels.
  // Customer set = CW from selected customer channels (default: GTM + Self-serve);
  // Partner excluded by default (partner-share deals are not end-customers).
  // Revenue = sum of deal amounts (each deal.amount is monthly MRR) − churned MRR + (optional) upsell MRR.
  // Upsells lift existing customers' value: they enter the NUMERATOR, never the denominator.
  // Avg tenure = mean(now − closeDate) over the same customer set (so LTV = arpu × tenure
  // is consistent across channels — change the dropdown, both numbers re-derive together).
  const economics = useMemo(() => {
    const allDeals = actuals?.deals || [];
    const nowMs = Date.now();
    const customerDeals = allDeals.filter(d =>
      STAGE_LABELS[d.stage]?.isClosedWon
      && customerCells.includes(d.cell)
      && d.closeDate && new Date(d.closeDate).getTime() <= nowMs
    );
    const churnedDeals = allDeals.filter(d =>
      STAGE_LABELS[d.stage]?.isClosedLost && customerCells.includes(d.cell)
    );
    const customerCount = Math.max(1, customerDeals.length - churnedDeals.length);
    const baseRevenue  = customerDeals.reduce((s, d) => s + (d.amount || 0), 0);
    const churnRevenue = churnedDeals.reduce((s, d) => s + (d.amount || 0), 0);
    // Link upsells to their base customer by company name. Without this, an upsell
    // for a customer in an UNCHECKED channel (e.g. BIGMAN with base in `otherActual`
    // when "Other" is off) would still inflate revenue while the base wasn't
    // counted — driving ARPU artificially up. Now: if the base isn't selected,
    // the upsell is dropped too.
    const customerKeys = new Set(customerDeals.map(customerKey).filter(Boolean));
    const allUpsells = allDeals.filter(d =>
      STAGE_LABELS[d.stage]?.isClosedWon && (d.cell === "setupUpsell" || d.cell === "upsellPipeline")
    );
    const eligibleUpsells = allUpsells.filter(d => customerKeys.has(customerKey(d)));
    const orphanUpsells = allUpsells.filter(d => !customerKeys.has(customerKey(d)));
    const upsellRevenue = includeUpsells
      ? eligibleUpsells.reduce((s, d) => s + (d.amount || 0), 0)
      : 0;
    const revenue = baseRevenue - churnRevenue + upsellRevenue;
    const arpu = revenue / customerCount;
    const tenuresMs = customerDeals
      .map(d => nowMs - new Date(d.closeDate).getTime())
      .filter(ms => ms > 0);
    const avgTenureMs = tenuresMs.length > 0 ? tenuresMs.reduce((s, m) => s + m, 0) / tenuresMs.length : 0;
    const avgTenureMonths = avgTenureMs / (1000 * 60 * 60 * 24 * 30.44);
    const ltv = arpu * Math.min(60, avgTenureMonths || 0);
    return {
      arpu,
      activeCount: customerCount,
      avgTenureMonths,
      ltv,
      baseRevenue,
      churnRevenue,
      upsellRevenue,
      upsellCount: eligibleUpsells.length,
      orphanUpsellCount: orphanUpsells.length,
      orphanUpsellRevenue: orphanUpsells.reduce((s, d) => s + (d.amount || 0), 0),
      hasData: customerDeals.length > 0,
    };
  }, [actuals?.deals, customerCells.join(","), includeUpsells]);

  // Quarters of FY26: Q1 = Jan-Mar, Q2 = Apr-Jun, Q3 = Jul-Sep, Q4 = Oct-Dec.
  const QUARTERS = [
    {idx: 0, label: "Q1", keys: ["jan","feb","mar"]},
    {idx: 1, label: "Q2", keys: ["apr","may","jun"]},
    {idx: 2, label: "Q3", keys: ["jul","aug","sep"]},
    {idx: 3, label: "Q4", keys: ["oct","nov","dec"]},
  ];
  const todayQtrIdx = QUARTERS.findIndex(q => q.keys.includes(today.monthKey));

  // Filter rows by timeframe
  const visibleRows = useMemo(() => {
    const tf = timeframe;
    if (tf.mode === "fy") return rows;
    if (tf.mode === "ytd") return rows.slice(0, (todayMonthIdxInRows + 1) || rows.length);
    if (tf.mode === "month") {
      const k = tf.monthKey || today.monthKey;
      const idx = window.MONTH_KEYS.indexOf(k);
      if (idx < 0) return rows;
      return rows.slice(idx, idx + 1);
    }
    if (tf.mode === "qtr") {
      const qIdx = (typeof tf.qtrIdx === "number") ? tf.qtrIdx : (todayQtrIdx >= 0 ? todayQtrIdx : 0);
      const q = QUARTERS[qIdx];
      if (!q) return rows;
      return rows.filter(r => q.keys.includes(r.key));
    }
    if (tf.mode === "custom") {
      const s = window.MONTH_KEYS.indexOf(tf.customStart || "feb");
      const e = window.MONTH_KEYS.indexOf(tf.customEnd || "dec");
      if (s < 0 || e < 0) return rows;
      return rows.slice(Math.min(s,e), Math.max(s,e) + 1);
    }
    return rows;
  }, [rows, timeframe, todayMonthIdxInRows, today.monthKey, todayQtrIdx]);

  // Per-timeframe scope summary (used by KPI logic)
  const scope = useMemo(() => {
    const first = visibleRows[0];
    const last = visibleRows[visibleRows.length - 1];
    const startEom = first ? (window.MONTH_KEYS.indexOf(first.key) > 0
      ? rows[window.MONTH_KEYS.indexOf(first.key) - 1].realEom ?? rows[window.MONTH_KEYS.indexOf(first.key) - 1].eomTarget
      : 0) : 0;
    const endEomTarget = last ? last.eomTarget : 0;
    const endEomReal = last ? (last.realEom ?? null) : null;
    const sumChurn = visibleRows.reduce((s, r) => s + (r.churnAbs || 0), 0);
    const sumNetAddTarget = endEomTarget - startEom;
    const sumNetAddReal = (endEomReal != null) ? (endEomReal - startEom) : null;
    const sumGrossAddTarget = sumNetAddTarget + sumChurn;
    const sumChannel = (key) => visibleRows.reduce((s, r) => s + (r[key] || 0), 0);
    return {
      startEom, endEomTarget, endEomReal,
      sumChurn,
      sumNetAddTarget, sumNetAddReal, sumGrossAddTarget,
      sumGtm: sumChannel("gtmTarget"),
      sumPartner: sumChannel("partnerMrr"),
      sumSelf: sumChannel("selfServeMrr"),
      sumOthers: sumChannel("othersTotal"),
      sumAeGap: sumChannel("aeChannelGap"),
      first, last,
      label: (() => {
        if (timeframe.mode === "fy") return state.lang === "it" ? "Anno fiscale 2026" : "Fiscal year 2026";
        if (timeframe.mode === "ytd") return state.lang === "it" ? "Year-to-date" : "Year-to-date";
        if (timeframe.mode === "month") {
          const mk = timeframe.monthKey || today.monthKey;
          return state.lang === "it" ? window.MONTH_LABELS_IT[mk] : window.MONTH_LABELS_EN[mk];
        }
        if (timeframe.mode === "qtr") {
          const q = QUARTERS[timeframe.qtrIdx ?? todayQtrIdx];
          return q ? `${q.label} 2026` : "Q?";
        }
        if (timeframe.mode === "custom") {
          const a = window.MONTH_SHORT[timeframe.customStart || "feb"];
          const b = window.MONTH_SHORT[timeframe.customEnd || "dec"];
          return `${a} → ${b}`;
        }
        return "";
      })(),
    };
  }, [visibleRows, rows, timeframe, today.monthKey, todayQtrIdx, state.lang]);

  // Total churn over visible window
  const totalChurnEur = visibleRows.reduce((s, r) => s + r.churnAbs, 0);
  const churnPct = currentMrr > 0 ? (rows[todayMonthIdxInRows >= 0 ? todayMonthIdxInRows : 0]?.churnPct || 0) : 0;

  // setTop + setScenario kept here — used outside the table (lang picker
  // farther down + scenario active toggle). Other setters moved into the
  // extracted modules: setScenarioField → <InputsPanel>; setMonth, setAeMonth,
  // setAeField, setAeCount → <ForecastTable> (derived from setState there).
  const setTop = (patch) => setState(s => ({...s, ...patch}));
  const setScenario = (which) => setState(s => ({...s, scenarios: {...(s.scenarios || {}), active: which}}));

  const toggleSeries = (k) => setHiddenSeries(h => ({...h, [k]: !h[k]}));

  // Track header height for sticky thead offset
  useEffect(() => {
    const el = document.querySelector(".head.sticky");
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--head-h", h + "px");
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, []);

  // Close timeframe submenu on click outside
  useEffect(() => {
    if (!tfMenuOpen) return;
    const onDoc = (e) => {
      if (!e.target.closest || !e.target.closest(".tf-item")) setTfMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [tfMenuOpen]);

  const resetAll = () => {
    // 2-step: prompt password, then confirm. Both via the custom modal.
    setModal({
      kind: "prompt",
      title: state.lang === "it" ? "Reset di tutti i valori" : "Reset all values",
      message: state.lang === "it"
        ? "Inserisci la password per resettare. L'azione non è reversibile."
        : "Enter the password to reset. This action cannot be undone.",
      confirmLabel: state.lang === "it" ? "Continua" : "Continue",
      inputType: "password",
      onConfirm: (pw) => {
        if (pw !== "Bardabing-04") {
          setModal(m => ({ ...m, error: state.lang === "it" ? "Password errata." : "Wrong password." }));
          return;
        }
        setModal({
          kind: "confirm",
          danger: true,
          title: state.lang === "it" ? "Confermi il reset?" : "Confirm reset?",
          message: state.lang === "it"
            ? "Tutti i target, gli AE, le note e gli edit verranno cancellati e tornano ai default. Non si può annullare."
            : "All targets, AEs, notes and edits will be cleared and reverted to defaults. Cannot be undone.",
          confirmLabel: state.lang === "it" ? "Sì, reset" : "Yes, reset",
          onConfirm: () => {
            const preservedLang = state.lang;
            localStorage.removeItem(STORAGE_KEY);
            const fresh = deepClone(window.DEFAULT_STATE);
            fresh.lang = preservedLang; // don't reset lang on reset
            setState(fresh);
            setModal(null);
          },
        });
      },
    });
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fonio-forecast-${new Date().toISOString().slice(0,10)}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportCsv = () => {
    // One row per month with target/real for each column. Italian-friendly: ; separator + comma decimal.
    const fmt = (n) => (n == null || isNaN(n)) ? "" : String(n).replace(".", ",");
    const headers = [
      "Mese","Stagionalità %","Tot MRR EOM Target","Tot MRR EOM Real",
      "Add MRR Target","Add MRR Real","Churn €","Churn %",
      "GTM Target","GTM Real","Partner Target","Partner Real",
      "Self-Serve Target","Self-Serve Real","Setup Upsell","Upsell Pipeline",
      "AE Channel Gap","AE necessari","AE attivi","SDR necessari",
    ];
    const labelMap = state.lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN;
    const lines = [headers.join(";")];
    for (const r of rows) {
      lines.push([
        labelMap[r.key] || r.key,
        fmt(r.seasonality),
        fmt(r.eomTarget), fmt(r.realEom),
        fmt(r.targetAdd), fmt(r.realAdd),
        fmt(r.churnAbs), fmt(r.churnPct.toFixed(2)),
        fmt(r.gtmTarget), fmt(r.gtmActual),
        fmt(r.partnerMrr), fmt(r.partnerActual),
        fmt(r.selfServeMrr), fmt(r.selfServeActual),
        fmt(r.setupUpsell), fmt(r.upsellPipeline),
        fmt(r.aeChannelGap), fmt(Math.round(r.aeHeadcountNeeded * 10) / 10),
        fmt(r.hiredAeCount), fmt(Math.round(r.sdrNeeded * 10) / 10),
      ].join(";"));
    }
    const csv = "﻿" + lines.join("\n"); // BOM so Excel italiano apre con encoding ok
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fonio-forecast-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // VIEW password gate — fullscreen modal blocks the dashboard until the user enters it.
  // Only shown when HubSpot integration is enabled (Worker URL configured).
  if (hubspotEnabled && !viewPassword) {
    return <PasswordGate onSubmit={(pw) => {
      try { localStorage.setItem("forecast_view_pw", pw); } catch (e) {}
      setViewPassword(pw);
      setAuthError(null);
      // The viewer fetch needs the new password; ask the provider to retry.
      // Use a short timeout so localStorage is flushed before the refetch.
      try { setTimeout(() => { __refreshViewer && __refreshViewer(); }, 0); } catch {}
    }} authError={authError} />;
  }

  // Phase 5.4 — soft identity picker. After password gate, before dashboard.
  // If no viewer is picked yet (or the picked id is missing/inactive),
  // <UserPicker /> takes the whole screen until a User is selected.
  // The viewer state lives in ViewerProvider (wired in src/main.tsx) so
  // any composed component can call useViewer() once we're past this gate.
  if (hubspotEnabled && !__viewerLoading && !__viewer) {
    return <UserPicker />;
  }
  if (hubspotEnabled && __viewerLoading) {
    // Brief flash while the provider's initial fetch resolves. Same visual
    // language as PasswordGate so the transition feels continuous.
    return (
      <div style={{
        position: "fixed", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--bg)", color: "var(--text-muted)", fontSize: "var(--fs-body)",
      }}>
        Loading viewer…
      </div>
    );
  }

  return (
    <EditModeContext.Provider value={editMode === true ? true : { ae: true }}>
    <div className="app">
      <header className="head sticky">
        {/* ROW 1 — brand (left) + KPI sticky (center, on scroll) + utility actions (right).
            Phase 3g: brand row collapses when head--scrolled (>220px scroll). */}
        <div className="head__top">
          {/* Phase B4b — was head__brand (logo + "Sales Forecast" + country
              label + sub-line). Sidebar carries the fonio brand now; topbar
              carries chrome on non-forecast routes. Here we keep only the
              page-scoped identity (country + flag) and the page-scoped meta
              (date + savedAt + HubSpot sync). PageHeader (level=2 so it
              doesn't compete with future page h1s) is the new primitive. */}
          <div className="head__brand">
            <PageHeader
              level={1}
              title={
                <span className="logo__country">
                  {(window as any).MARKET_CONFIG?.label || "Italy"}{" "}
                  <span className="logo__flag">{(window as any).MARKET_CONFIG?.flag || "🇮🇹"}</span>
                </span>
              }
              meta={
                <>
                  <span className="today-date">{today.dateStr}</span>
                  {savedAt && <span className="saved-pill"> · {state.lang === "it" ? "Salvato" : "Saved"} {savedAt.toLocaleTimeString()}</span>}
                  {hubspotEnabled && kvSavedAt && (
                    <Tooltip tip={state.lang === "it" ? "Sincronizzato col team via Cloudflare KV" : "Synced with team via Cloudflare KV"}>
                      <span className="hs-saved">
                        {" · ☁ "}{state.lang === "it" ? "sync" : "synced"} {relativeTime(kvSavedAt.toISOString(), state.lang)}
                      </span>
                    </Tooltip>
                  )}
                  {hubspotEnabled && !actuals?.fetchedAt && actualsLoading && (
                    <span className="hs-fetched">
                      {" · "}<span className="skeleton-bar" style={{width: 80}} />
                    </span>
                  )}
                  {hubspotEnabled && actuals?.fetchedAt && (
                    <Tooltip tip={`HubSpot · ${actuals.fromCache ? "cache" : "fresh"}`}>
                      <span className="hs-fetched">
                        {" · "}HubSpot {relativeTime(actuals.fetchedAt, state.lang)}
                        {actuals.fromCache ? " · cache" : ""}
                      </span>
                    </Tooltip>
                  )}
                </>
              }
            />
          </div>
          {/* PR 3 — sticky target chip restored. The HeroKpi at the top of
              the page is great when you're at the top, but disappears on
              scroll. This compact chip slots into the head__top center
              slot when scrolled past 220px, showing target + status pill
              so the answer to "am I on track?" is always one glance away. */}
          <StickyTargetChip
            visible={scrolledPast}
            label={state.lang === "it" ? "Target Dic 2026" : "Dec 2026 target"}
            value={globalEur(targetMrr)}
            trackStatus={trackStatus}
            statusLabel={
              trackStatus === "on" ? (state.lang === "it" ? "On target" : "On target")
              : trackStatus === "ahead" ? (state.lang === "it" ? `Avanti ${aheadPct.toFixed(0)}%` : `Ahead ${aheadPct.toFixed(0)}%`)
              : (state.lang === "it" ? `Indietro ${Math.abs(aheadPct).toFixed(0)}%` : `Behind ${Math.abs(aheadPct).toFixed(0)}%`)
            }
          />
          <div className="head__actions">
            <ViewerChip compact />
            {hubspotEnabled && (
              <Button variant="ghost" size="md"
                tone={actualsError ? "warn" : undefined}
                loading={actualsLoading} disabled={actualsLoading}
                onClick={() => fetchActuals(true)}
                title={actualsError
                  ? (state.lang === "it" ? `Errore: ${actualsError}. Clicca per ritentare` : `Error: ${actualsError}. Click to retry`)
                  : (state.lang === "it" ? "Aggiorna i dati real da HubSpot" : "Refresh actuals from HubSpot")}>
                {actualsLoading ? (
                  <>⟳ {state.lang === "it" ? "Aggiorno" : "Refreshing"}{actualsLoadingProgress ? <span className="btn-refresh__progress">{actualsLoadingProgress.done}/{actualsLoadingProgress.total}</span> : "…"}</>
                ) : actualsError ? <>⚠ {state.lang === "it" ? "Riprova" : "Retry"}</> : <>⟳ {state.lang === "it" ? "Aggiorna" : "Refresh"}</>}
              </Button>
            )}
            {/* Notification bell — recent CW/CL events with unread tracking via localStorage */}
            <NotificationBell
              deals={actuals?.deals || []}
              owners={actuals?.owners || {}}
              portalId={actuals?.portalId || null}
              lang={state.lang}
              fmtMoney={(n) => globalEur(n)}
              notifSound={notifSound}
              notifDuration={notifDuration}
            />
            <Button variant="ghost" size="md" active={editMode}
              onClick={() => {
                if (editMode) { setEditMode(false); setEditPassword(""); return; }
                setModal({
                  kind: "prompt",
                  title: state.lang === "it" ? "Sblocca modifica" : "Unlock editing",
                  message: state.lang === "it" ? "Inserisci la password per sbloccare le modifiche complete." : "Enter the password to unlock full editing.",
                  confirmLabel: state.lang === "it" ? "Sblocca" : "Unlock",
                  inputType: "password",
                  onConfirm: (pw) => {
                    if (pw === "Bardabing-04") { setEditMode(true); setEditPassword(pw); setModal(null); }
                    else setModal(m => ({ ...m, error: state.lang === "it" ? "Password errata." : "Wrong password." }));
                  },
                });
              }}
              title={editMode ? (state.lang === "it" ? "Modifica attiva — clicca per bloccare" : "Editing on — click to lock") : (state.lang === "it" ? "Sblocca modifica (richiede password)" : "Unlock editing (password required)")}>
              {editMode
                ? (state.lang === "it" ? "🔓 Modifica attiva" : "🔓 Editing on")
                : (state.lang === "it" ? "🔒 Solo lettura" : "🔒 View only")}
            </Button>
            {/* Phase B4b: theme switch removed — AppTopbar carries the global
                theme toggle now, and both surfaces share state via lib/theme.ts.
                On forecast routes the topbar is suppressed, so we render a
                small theme button HERE inside head__actions to preserve the
                affordance. Reuses the same useTheme() hook ForecastItaly already
                subscribes to (lines 770-774). */}
            {(() => {
              const next = theme === "dark" ? "light" : "dark";
              return (
                <Button
                  variant="ghost" size="md" iconOnly
                  onClick={() => setTheme(next)}
                  aria-label={theme === "dark"
                    ? (state.lang === "it" ? "Passa a tema chiaro" : "Switch to light theme")
                    : (state.lang === "it" ? "Passa a tema scuro" : "Switch to dark theme")}
                  title={state.lang === "it"
                    ? (theme === "dark" ? "Tema chiaro" : "Tema scuro")
                    : (theme === "dark" ? "Light theme" : "Dark theme")}>
                  {theme === "dark" ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="4" />
                      <line x1="12" y1="2"  x2="12" y2="5"/>
                      <line x1="12" y1="19" x2="12" y2="22"/>
                      <line x1="2"  y1="12" x2="5"  y2="12"/>
                      <line x1="19" y1="12" x2="22" y2="12"/>
                      <line x1="4.5"  y1="4.5"  x2="6.5"  y2="6.5"/>
                      <line x1="17.5" y1="17.5" x2="19.5" y2="19.5"/>
                      <line x1="4.5"  y1="19.5" x2="6.5"  y2="17.5"/>
                      <line x1="17.5" y1="6.5"  x2="19.5" y2="4.5"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 12.5A9 9 0 1 1 11.5 3a7 7 0 0 0 9.5 9.5z" />
                    </svg>
                  )}
                </Button>
              );
            })()}
            {/* Notification sound mute toggle — clicks toggle "off" ↔ previous preset.
                Plays a short preview when un-muting so user knows what they signed up for. */}
            <Button variant="ghost" size="md" iconOnly
              className={notifSound === "off" ? "btn--muted" : ""}
              onClick={() => {
                if (notifSound === "off") {
                  const restore = (() => {
                    try { return localStorage.getItem("forecast_notif_sound_last") || "ding-dong"; } catch { return "ding-dong"; }
                  })();
                  setNotifSoundSaved(restore);
                  playNotifSound(restore, "normal");
                } else {
                  try { localStorage.setItem("forecast_notif_sound_last", notifSound); } catch {}
                  setNotifSoundSaved("off");
                }
              }}
              title={notifSound === "off"
                ? (state.lang === "it" ? "Suoni notifica disattivati — clicca per riattivare" : "Notification sounds muted — click to unmute")
                : (state.lang === "it" ? `Suono: ${NOTIF_SOUND_PRESETS[notifSound]?.label?.it || notifSound} — clicca per silenziare` : `Sound: ${NOTIF_SOUND_PRESETS[notifSound]?.label?.en || notifSound} — click to mute`)}>
              {notifSound === "off" ? "🔇" : "🔊"}
            </Button>
            {/* Settings dropdown — JSON / CSV / password VIEW / Logout / Inputs (admin) / Reset all live here */}
            <div className="settings-wrap">
              <Button variant="ghost" size="md" iconOnly onClick={() => setSettingsOpen(o => !o)}
                title={state.lang === "it" ? "Impostazioni" : "Settings"}
                aria-expanded={settingsOpen} aria-haspopup="true">⚙</Button>
              {settingsOpen && (
                <>
                  <div className="settings-backdrop" onClick={() => setSettingsOpen(false)} />
                  <div className="settings-menu" role="menu">
                    <button className="settings-menu__item" onClick={() => { exportCsv(); setSettingsOpen(false); }}>
                      <span>↓</span> {state.lang === "it" ? "Esporta CSV" : "Export CSV"}
                    </button>
                    <button className="settings-menu__item" onClick={() => { exportJson(); setSettingsOpen(false); }}>
                      <span>↓</span> {state.lang === "it" ? "Esporta JSON" : "Export JSON"}
                    </button>
                    <div className="settings-menu__sep" />
                    {/* Modifica password VIEW — admin-gated, calls Worker to update KV-stored override */}
                    <button className="settings-menu__item" onClick={() => {
                      setSettingsOpen(false);
                      setModal({
                        kind: "prompt",
                        title: state.lang === "it" ? "Modifica password VIEW" : "Change VIEW password",
                        message: state.lang === "it" ? "1) Inserisci la password admin per autorizzare il cambio." : "1) Enter the admin password to authorize the change.",
                        confirmLabel: state.lang === "it" ? "Avanti" : "Next",
                        inputType: "password",
                        onConfirm: (adminPw) => {
                          if (adminPw !== "Bardabing-04") {
                            setModal(m => ({...m, error: state.lang === "it" ? "Password admin errata." : "Wrong admin password."}));
                            return;
                          }
                          setModal({
                            kind: "prompt",
                            title: state.lang === "it" ? "Nuova password VIEW" : "New VIEW password",
                            message: state.lang === "it" ? "2) Inserisci la nuova password che i colleghi useranno per accedere." : "2) Enter the new password that colleagues will use to log in.",
                            confirmLabel: state.lang === "it" ? "Aggiorna" : "Update",
                            inputType: "text",
                            onConfirm: async (newPw) => {
                              if (!newPw || newPw.length < 4) {
                                setModal(m => ({...m, error: state.lang === "it" ? "Min 4 caratteri." : "Min 4 chars."}));
                                return;
                              }
                              try {
                                const resp = await fetch(`${WORKER_URL}/api/auth/view-password`, {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                    "X-View-Password": viewPassword,
                                    "X-Edit-Password": adminPw,
                                  },
                                  body: JSON.stringify({ newPassword: newPw }),
                                });
                                if (!resp.ok) {
                                  const txt = await resp.text();
                                  setModal(m => ({...m, error: `${state.lang === "it" ? "Errore" : "Error"}: ${txt.slice(0, 100)}`}));
                                  return;
                                }
                                // Update local copy of view pw to match new value
                                setViewPassword(newPw);
                                try { localStorage.setItem("forecast_view_pw", newPw); } catch {}
                                setModal({
                                  kind: "alert",
                                  title: state.lang === "it" ? "✓ Password aggiornata" : "✓ Password updated",
                                  message: state.lang === "it" ? `La nuova password VIEW è "${newPw}". Comunicala ai colleghi — la vecchia non funziona più.` : `The new VIEW password is "${newPw}". Share with colleagues — the old one no longer works.`,
                                  confirmLabel: "OK",
                                  onConfirm: () => setModal(null),
                                });
                              } catch (e) {
                                setModal(m => ({...m, error: state.lang === "it" ? "Network error." : "Network error."}));
                              }
                            },
                          });
                        },
                      });
                    }}>
                      <span>🔑</span> {state.lang === "it" ? "Cambia password VIEW" : "Change VIEW password"}
                    </button>
                    {/* Sound preset picker — opens a modal listing all 5 presets with a preview play button */}
                    <button className="settings-menu__item" onClick={() => {
                      setSettingsOpen(false);
                      setModal({
                        kind: "sound-picker",
                        title: state.lang === "it" ? "Suono notifiche" : "Notification sound",
                        confirmLabel: state.lang === "it" ? "Chiudi" : "Close",
                        onConfirm: () => setModal(null),
                      });
                    }}>
                      <span>🔊</span> {state.lang === "it" ? "Suono notifiche" : "Notification sound"}
                    </button>
                    <div className="settings-menu__sep" />
                    {/* PR 1 — Design preset picker. 4 inline buttons; click
                        switches the platform's overall style instantly via
                        the [data-design] attribute on <html>. Persists to
                        localStorage. Closes the dropdown so the user can
                        see the change. */}
                    <div className="settings-menu__group">
                      <div className="settings-menu__group-label">
                        {state.lang === "it" ? "Stile" : "Design"}
                      </div>
                      <div className="settings-design-grid">
                        {DESIGNS.map(d => (
                          <button
                            key={d}
                            type="button"
                            className={`settings-design-btn ${design === d ? "settings-design-btn--active" : ""}`}
                            onClick={() => { setDesignPref(d); setSettingsOpen(false); }}
                            aria-pressed={design === d}
                          >
                            {state.lang === "it" ? DESIGN_LABELS[d].it : DESIGN_LABELS[d].en}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="settings-menu__sep" />
                    <button className="settings-menu__item" onClick={() => {
                      setSettingsOpen(false);
                      setModal({
                        kind: "confirm",
                        title: state.lang === "it" ? "Logout" : "Logout",
                        message: state.lang === "it" ? "Vuoi disconnetterti? Dovrai reinserire la VIEW password al prossimo accesso." : "Log out? You'll need to re-enter the VIEW password next time.",
                        confirmLabel: state.lang === "it" ? "Logout" : "Logout",
                        onConfirm: () => {
                          try { localStorage.removeItem("forecast_view_pw"); } catch {}
                          setViewPassword("");
                          setEditMode(false); setEditPassword("");
                          setActuals(null); setHasInitialActuals(false);
                          setModal(null);
                        },
                      });
                    }}>
                      <span>⏻</span> {state.lang === "it" ? "Logout" : "Logout"}
                    </button>
                    <button className="settings-menu__item" onClick={() => {
                      setSettingsOpen(false);
                      setModal({
                        kind: "prompt",
                        title: state.lang === "it" ? "Inputs admin" : "Admin inputs",
                        message: state.lang === "it" ? "Inserisci la password admin per modificare assunzioni e scenari." : "Enter the admin password to edit assumptions and scenarios.",
                        confirmLabel: state.lang === "it" ? "Sblocca" : "Unlock",
                        inputType: "password",
                        onConfirm: (pw) => {
                          if (pw === "Bardabing-04") {
                            setEditMode(true); setEditPassword(pw);
                            setInputsPanelOpen(true);
                            setModal(null);
                          } else {
                            setModal(m => ({...m, error: state.lang === "it" ? "Password errata." : "Wrong password."}));
                          }
                        },
                      });
                    }}>
                      <span>⚙</span> {state.lang === "it" ? "Inputs e assunzioni" : "Inputs & assumptions"}
                    </button>
                    <div className="settings-menu__sep" />
                    <button className="settings-menu__item settings-menu__item--danger" onClick={() => { setSettingsOpen(false); resetAll(); }}>
                      <span>↺</span> {state.lang === "it" ? "Reset (richiede password)" : "Reset (password required)"}
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* PR 2 — was an IT|EN segmented. Replaced with the LangMenu
                dropdown so the lang control matches the topbar's pattern
                + scales to more languages later. Writes via lib/lang.ts
                setLang(), which broadcasts and ForecastMarket's existing
                subscriber syncs state.lang on the event. */}
            <LangMenu />
          </div>
        </div>
        {/* ROW 2 — display controls (MRR/ARR, Scenari, Timeframe). Phase B4b
            graduated this from a bespoke head__row-2-controls flex div to a
            v2 Toolbar primitive (borderless because .head already provides
            the visual divider above the content area). Controls render as
            sequential children, wrapping if the viewport narrows. */}
        <Toolbar borderless className="head__row-2">
            <Segmented
              size="sm"
              ariaLabel={state.lang === "it" ? "Modalità valore" : "Value mode"}
              title={state.lang === "it" ? "Visualizza come MRR o ARR" : "Display as MRR or ARR"}
              value={valueMode}
              options={[
                {id: "mrr", label: "MRR", title: "Monthly Recurring Revenue"},
                {id: "arr", label: "ARR", title: "Annual Recurring Revenue (×12)"},
              ]}
              onChange={setValueMode}
            />
            <ScenariosBar lang={state.lang} activeScenario={activeScenario} onChange={setScenario} />
            {(() => {
              // Build the timeframe options. Active label expands to show context
              // ("Mese · Mag", "Trim · Q2", "Feb → Dec") so the segmented carries
              // its own state in-line. Three items are expandable — clicking
              // their active button opens a dropdown for picking the specific value.
              const tfLabels = {
                month: state.lang === "it" ? "Mese" : "Month",
                qtr:   state.lang === "it" ? "Trim." : "Qtr",
                ytd:   "YTD",
                fy:    "FY",
                custom: state.lang === "it" ? "Custom" : "Custom",
              };
              const activeMonthLabel = (() => {
                if (timeframe.mode !== "month") return tfLabels.month;
                const mk = timeframe.monthKey || today.monthKey;
                return `${tfLabels.month} · ${window.MONTH_SHORT[mk]}`;
              })();
              const activeQtrLabel = (() => {
                if (timeframe.mode !== "qtr") return tfLabels.qtr;
                const q = QUARTERS[timeframe.qtrIdx ?? todayQtrIdx] || QUARTERS[0];
                return `${tfLabels.qtr} · ${q.label}`;
              })();
              const activeCustomLabel = (() => {
                if (timeframe.mode !== "custom") return tfLabels.custom;
                if (timeframe.customStart && timeframe.customEnd) {
                  return `${window.MONTH_SHORT[timeframe.customStart]} → ${window.MONTH_SHORT[timeframe.customEnd]}`;
                }
                return tfLabels.custom;
              })();
              const tfOptions = [
                { id: "month",  label: activeMonthLabel,  expandable: true },
                { id: "qtr",    label: activeQtrLabel,    expandable: true },
                { id: "ytd",    label: tfLabels.ytd },
                { id: "fy",     label: tfLabels.fy },
                { id: "custom", label: activeCustomLabel, expandable: true },
              ];
              const onTimeframeChange = (id) => {
                if (id === "month")  setTimeframe({ mode: "month", monthKey: timeframe.monthKey || today.monthKey });
                else if (id === "qtr") setTimeframe({ mode: "qtr", qtrIdx: timeframe.qtrIdx ?? Math.max(0, todayQtrIdx) });
                else if (id === "custom") setTimeframe({ mode: "custom", customStart: timeframe.customStart || "feb", customEnd: timeframe.customEnd || "dec" });
                else setTimeframe({ mode: id });
              };
              return (
                <Segmented
                  size="sm"
                  ariaLabel={state.lang === "it" ? "Periodo" : "Timeframe"}
                  value={timeframe.mode}
                  options={tfOptions}
                  onChange={onTimeframeChange}
                  expandedId={tfMenuOpen}
                  onToggleExpand={setTfMenuOpen}
                  renderDropdown={(opt) => {
                    if (opt.id === "month") {
                      return window.MONTH_KEYS.map(mk => (
                        <button
                          key={mk}
                          className={`tf-menu__item ${(timeframe.monthKey || today.monthKey) === mk ? "on" : ""}`}
                          onClick={() => { setTimeframe({mode: "month", monthKey: mk}); setTfMenuOpen(null); }}
                        >
                          {state.lang === "it" ? window.MONTH_LABELS_IT[mk] : window.MONTH_LABELS_EN[mk]}
                          {mk === today.monthKey && <span className="tf-menu__pill">now</span>}
                        </button>
                      ));
                    }
                    if (opt.id === "qtr") {
                      return QUARTERS.map(q => (
                        <button
                          key={q.idx}
                          className={`tf-menu__item ${(timeframe.qtrIdx ?? todayQtrIdx) === q.idx ? "on" : ""}`}
                          onClick={() => { setTimeframe({mode: "qtr", qtrIdx: q.idx}); setTfMenuOpen(null); }}
                        >
                          <b>{q.label}</b> · {q.keys.map(k => window.MONTH_SHORT[k]).join(" / ")}
                          {q.idx === todayQtrIdx && <span className="tf-menu__pill">now</span>}
                        </button>
                      ));
                    }
                    if (opt.id === "custom") {
                      return (
                        <div className="tf-menu--custom">
                          <div className="tf-menu__row">
                            <label>{state.lang === "it" ? "Da" : "From"}</label>
                            <select value={timeframe.customStart || "feb"} onChange={e => setTimeframe({...timeframe, customStart: e.target.value})}>
                              {window.MONTH_KEYS.map(mk => (
                                <option key={mk} value={mk}>{state.lang === "it" ? window.MONTH_LABELS_IT[mk] : window.MONTH_LABELS_EN[mk]}</option>
                              ))}
                            </select>
                          </div>
                          <div className="tf-menu__row">
                            <label>{state.lang === "it" ? "A" : "To"}</label>
                            <select value={timeframe.customEnd || "dec"} onChange={e => setTimeframe({...timeframe, customEnd: e.target.value})}>
                              {window.MONTH_KEYS.map(mk => (
                                <option key={mk} value={mk}>{state.lang === "it" ? window.MONTH_LABELS_IT[mk] : window.MONTH_LABELS_EN[mk]}</option>
                              ))}
                            </select>
                          </div>
                          <button className="tf-menu__close" onClick={() => setTfMenuOpen(null)}>{state.lang === "it" ? "Applica" : "Apply"}</button>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              );
            })()}
        </Toolbar>
      </header>

      {hubspotEnabled && actualsError && (
        <div className="err-banner" role="alert">
          <span className="err-banner__icon">⚠️</span>
          <div className="err-banner__body">
            <div className="err-banner__title">
              {state.lang === "it" ? "Errore nel caricamento dati HubSpot" : "Failed to load HubSpot data"}
            </div>
            <div className="err-banner__detail">
              {actualsError}. {state.lang === "it"
                ? "I dati mostrati potrebbero essere obsoleti o di default."
                : "The data shown may be stale or default."}
            </div>
          </div>
          <button className="err-banner__action" onClick={() => fetchActuals(true)}>
            {state.lang === "it" ? "Riprova" : "Retry"}
          </button>
        </div>
      )}

      {(() => {
        const pending = (state.aes || []).filter(ae => ae.person && !ae.hubspotOwnerId);
        if (pending.length === 0) return null;
        const names = pending.map(ae => ae.person).join(", ");
        return (
          <div className="hs-reminder" role="status">
            <span className="hs-reminder__bell" aria-hidden="true">🔔</span>
            <div className="hs-reminder__body">
              <b>{state.lang === "it" ? "Collega HubSpot per i nuovi AE" : "Connect HubSpot for new AEs"}</b>
              <span className="hs-reminder__detail">
                {state.lang === "it"
                  ? `Hai assegnato ${pending.length === 1 ? "un nome" : "dei nomi"} (${names}) ma manca il collegamento al loro hubspot_owner_id. Senza, la colonna "Real" per quell'AE non si popolerà da HubSpot.`
                  : `You assigned ${pending.length === 1 ? "a name" : "names"} (${names}) but the hubspot_owner_id link is missing. The "Real" column for that AE won't populate from HubSpot until it's wired up.`}
              </span>
            </div>
          </div>
        );
      })()}

      {modal && modal.kind !== "sound-picker" && (
        <Modal open={true} onClose={() => setModal(null)}
          title={modal.title} message={modal.message}
          variant={modal.kind === "prompt" ? "prompt" : modal.kind === "alert" ? "alert" : "confirm"}
          confirmLabel={modal.confirmLabel} cancelLabel={modal.cancelLabel}
          danger={modal.danger} defaultValue={modal.defaultValue || ""}
          inputType={modal.inputType} error={modal.error}
          onConfirm={modal.onConfirm}
          onClearError={() => setModal(m => m ? { ...m, error: null } : m)} />
      )}
      {/* Sound preset picker — modal listing all presets + Duration + Mic recorder. */}
      {modal && modal.kind === "sound-picker" && (
        <SoundPickerModal
          lang={state.lang}
          notifSound={notifSound} setNotifSound={setNotifSoundSaved}
          notifDuration={notifDuration} setNotifDuration={setNotifDurationSaved}
          hasCustomAudio={hasCustomAudio} setHasCustomAudio={setHasCustomAudio}
          onClose={() => setModal(null)}
          title={modal.title} confirmLabel={modal.confirmLabel} />
      )}

      {/* Phase C1 — Hero KPI. Lives above the KpiStrip; uses ForecastItaly's
          already-computed numbers (lines ~1142-1161). The labels resolve via
          window.MONTH_LABELS_IT/EN for both the FY-end target month (always
          Dec for now) and the last-closed month label. */}
      {(() => {
        const monthLabels = state.lang === "it" ? window.MONTH_LABELS_IT : window.MONTH_LABELS_EN;
        const targetMonthLabel = state.lang === "it" ? `${monthLabels.dec} 2026` : `${monthLabels.dec} 2026`;
        const lastClosedLabel = lastClosed ? monthLabels[lastClosed.key] : undefined;
        // Reuse the global money formatter so MRR/ARR multiplication stays
        // consistent with the rest of the page (globalEur already applies
        // the valueMode multiplier).
        return (
          <HeroKpi
            currentMrr={currentMrr}
            targetMrr={targetMrr}
            targetMonthLabel={targetMonthLabel}
            monthsLeft={monthsLeft}
            aheadPct={aheadPct}
            trackStatus={trackStatus}
            lastClosed={lastClosed ? { key: lastClosed.key, realEom: lastClosed.realEom } : null}
            lastClosedLabel={lastClosedLabel}
            lang={state.lang}
            fmtMoney={(n) => globalEur(n)}
          />
        );
      })()}

      {/* Phase G — Compare Scenarios. Three-card grid showing Optimistic /
          Base / Pessimistic side-by-side. The three row-arrays already
          live in `compareScenarios` (useMemo above), feeding the chart's
          comparison lines; this surface adds the narrative view ("to hit
          Optimistic, you need €X/mo") without the user having to read
          scaled chart lines. */}
      <CompareScenarios
        base={compareScenarios.base}
        optimistic={compareScenarios.optimistic}
        pessimistic={compareScenarios.pessimistic}
        currentMrr={currentMrr}
        monthsLeft={monthsLeft}
        lang={state.lang}
        fmtMoney={(n) => globalEur(n)}
      />

      <section className="kpis">
        {/* KPI strip — extracted into kpi-strip.jsx (Phase B sub-PR 4).
            Two render branches by mode: FY/YTD (4 tiles) or month/qtr/custom
            (3 tiles). All deriveds are passed as props for memo-readiness in
            Phase D. The internal data-prep (cumulativeChurn, channels[],
            statusEmoji, etc.) lives inside KpiStrip*. */}
        <KpiStrip
          mode={timeframe.mode}
          lang={state.lang}
          valueMode={valueMode}
          arpuChannels={arpuChannels}
          includeUpsells={includeUpsells}
          setState={setState}
          rows={rows}
          lastClosed={lastClosed}
          todayMonthIdxInRows={todayMonthIdxInRows}
          currentMrr={currentMrr}
          expectedNow={expectedNow}
          targetMrr={targetMrr}
          gap={gap}
          monthsLeft={monthsLeft}
          avgGrowth={avgGrowth}
          aheadDelta={aheadDelta}
          aheadPct={aheadPct}
          trackStatus={trackStatus}
          economics={economics}
          aes={state.aes}
          scope={scope}
          visibleRows={visibleRows}
        />
      </section>

      <Card padding="none" elevation="flat" collapsed={!sections.chart}
        className={`card chart-card ${chartExpanded ? "chart-card--expanded" : "chart-card--compact"} ${sections.chart ? "" : "card--collapsed"}`}>
        <SectionHeader
          title={lbl(state.lang === "it" ? "Traiettoria MRR" : "MRR trajectory")}
          open={sections.chart}
          onToggle={() => toggleSection("chart")}
        >
          <div className="chart-controls">
            {timeframe.mode !== "month" && (
              <div className="seg-toggle" title={state.lang === "it" ? "Modalità barre" : "Bar mode"}>
              <button
                className={`seg-toggle__btn ${barMode === "stacked" ? "on" : ""}`}
                onClick={() => setBarMode("stacked")}
                title={state.lang === "it" ? "Barre impilate" : "Stacked bars"}
              >
                <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
                  <rect x="2" y="2" width="3" height="9" fill="currentColor" opacity="0.5"/>
                  <rect x="2" y="6" width="3" height="5" fill="currentColor"/>
                  <rect x="9" y="3" width="3" height="8" fill="currentColor" opacity="0.5"/>
                  <rect x="9" y="7" width="3" height="4" fill="currentColor"/>
                </svg>
              </button>
              <button
                className={`seg-toggle__btn ${barMode === "grouped" ? "on" : ""}`}
                onClick={() => setBarMode("grouped")}
                title={state.lang === "it" ? "Barre affiancate" : "Grouped bars"}
              >
                <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
                  <rect x="1" y="4" width="2" height="7" fill="currentColor"/>
                  <rect x="4" y="2" width="2" height="9" fill="currentColor" opacity="0.7"/>
                  <rect x="8" y="6" width="2" height="5" fill="currentColor"/>
                  <rect x="11" y="3" width="2" height="8" fill="currentColor" opacity="0.7"/>
                </svg>
              </button>
            </div>
            )}
            {timeframe.mode !== "month" && (
            <Button variant="ghost" size="sm" active={logScale}
              onClick={() => setLogScale(s => !s)}
              title={state.lang === "it" ? "Scala logaritmica" : "Log scale"}
            >
              log
            </Button>
            )}
            {timeframe.mode !== "month" && (
            <Button variant="ghost" size="sm" active={yAxisLocked}
              onClick={() => setYAxisLocked(s => !s)}
              title={state.lang === "it"
                ? "Asse fisso: la scala verticale resta uguale anche cambiando timeframe (mese/trim/anno) o scenario, così i confronti sono visivamente comparabili. Spegnilo per riadattare automaticamente la scala al periodo visibile."
                : "Fixed axis: the vertical scale stays the same when you switch timeframe (month/qtr/year) or scenario, so comparisons stay visually consistent. Turn it off to auto-fit the scale to the visible period."}
            >
              {yAxisLocked
                ? (state.lang === "it" ? "🔒 Asse fisso" : "🔒 Fixed axis")
                : (state.lang === "it" ? "🔓 Asse libero" : "🔓 Auto axis")}
            </Button>
            )}
          </div>
          {/* Scenario selection — sits as a SECOND row of chart-controls, just under log/Asse fisso.
              0 selected → show all 3 (compare); 1+ → only those highlighted. Label "show scenario" */}
          <div className="chart-scn-row">
            <span className="chart-scn-row__label">
              {state.lang === "it" ? "show scenario" : "show scenario"}
            </span>
            <div className="seg-toggle seg-toggle--sm seg-toggle--scn-chart"
              title={state.lang === "it" ? "Mostra linee scenario sul grafico (vuoto = mostra tutti)" : "Show scenario lines on chart (empty = show all)"}>
              <button className={`seg-toggle__btn ${chartScenarios.includes("pessimistic") ? "on on--pess" : ""}`}
                onClick={(e) => {e.stopPropagation(); toggleChartScn("pessimistic");}} title={state.lang === "it" ? "Pessimistico" : "Pessimistic"}>😟</button>
              <button className={`seg-toggle__btn ${chartScenarios.includes("base") ? "on on--base" : ""}`}
                onClick={(e) => {e.stopPropagation(); toggleChartScn("base");}} title="Base">📊</button>
              <button className={`seg-toggle__btn ${chartScenarios.includes("optimistic") ? "on on--opt" : ""}`}
                onClick={(e) => {e.stopPropagation(); toggleChartScn("optimistic");}} title={state.lang === "it" ? "Ottimistico" : "Optimistic"}>🚀</button>
            </div>
          </div>
        </SectionHeader>
        {sections.chart && (<>
        {timeframe.mode === "month" && visibleRows.length === 1 ? (
          <MonthFocusChart row={visibleRows[0]} lang={state.lang} height={chartExpanded ? 460 : 220} />
        ) : (
          <GrowthChart
            rows={visibleRows}
            target={targetMrr}
            lang={state.lang}
            hiddenSeries={hiddenSeries}
            logScale={logScale}
            barMode={barMode}
            mode={timeframe.mode}
            expanded={chartExpanded}
            lastLockedKey={lastClosed?.key}
            todayMonthKey={today.monthKey}
            todayMonthFraction={today.monthFraction}
            valueMode={valueMode}
            yAxisLocked={yAxisLocked}
            activeScenario={activeScenario}
            compareScenarios={compareScenarios}
            chartScenarios={chartScenarios}
            fyMax={Math.max(targetMrr, allScenariosMax, ...rows.map(r => Math.max(r.realEom || 0, r.eomTarget || 0)))}
          />
        )}
        {timeframe.mode !== "month" && (
        <div className="legend legend--clickable">
          {[
            {k:"realLine",  label: lbl("Real MRR"),     swatch:"solid", color:"var(--proj)"},
            {k:"targetLine",label: lbl("Target MRR"), swatch:"dash",  color:"var(--lilac-700)"},
            ...(chartExpanded ? [
              {k:"gtm",     label: "GTM",         swatch:"box", color:"var(--lilac-700)"},
              {k:"ae",      label: "AE",          swatch:"box", color:"var(--ae-500)"},
              {k:"partner", label: "Partner",     swatch:"box", color:"var(--lilac-400)"},
              {k:"self",    label: "Self-Serve",  swatch:"box", color:"var(--lilac-300)"},
              {k:"others",  label: "Others",      swatch:"box", color:"var(--lilac-200)"},
              {k:"churn",   label: "Churn",       swatch:"box", color:"var(--danger-soft)"},
            ] : [])
          ].map(item => (
            <button
              key={item.k}
              className={`legend__item legend__item--btn ${hiddenSeries[item.k] ? "off" : ""}`}
              onClick={() => toggleSeries(item.k)}
            >
              {/* Phase 3f — swatch via v2 primitive. Old swatch types map:
                  "box" + "solid" → filled, "dash" → dashed. Color stays via
                  the explicit escape hatch since the legend mixes brand
                  tokens (--ch-*, --proj) with non-channel ones (--lilac-400). */}
              <V2Swatch
                variant={item.swatch === "dash" ? "dashed" : "filled"}
                color={item.color}
                size={10}
              />
              {item.label}
            </button>
          ))}
        </div>
        )}
        </>)}
      </Card>

      <Card padding="none" elevation="flat" collapsed={!sections.aeChart}
        className={`card chart-card chart-card--expanded ${sections.aeChart ? "" : "card--collapsed"}`}>
        <SectionHeader
          title={state.lang === "it" ? "AE: confronto mensile" : "AE comparison by month"}
          subtitle={(aeChartValueMode === "arr") ? (state.lang === "it" ? "Real ARR per AE per mese" : "Real ARR per AE per month") : (state.lang === "it" ? "Real MRR per AE per mese" : "Real MRR per AE per month")}
          open={sections.aeChart}
          onToggle={() => toggleSection("aeChart")}
        >
          <div className="seg-toggle seg-toggle--sm" title={state.lang === "it" ? "Mostra valori in MRR o ARR (solo questo grafico)" : "Show values in MRR or ARR (this chart only)"}>
            <button className={`seg-toggle__btn ${aeChartValueMode === "mrr" ? "on" : ""}`} onClick={(e) => {e.stopPropagation(); setAeChartValueMode("mrr");}}>MRR</button>
            <button className={`seg-toggle__btn ${aeChartValueMode === "arr" ? "on" : ""}`} onClick={(e) => {e.stopPropagation(); setAeChartValueMode("arr");}}>ARR</button>
          </div>
        </SectionHeader>
        {sections.aeChart && (
          <AEComparisonChart
            rows={visibleRows}
            aes={state.aes || []}
            lang={state.lang}
            valueMode={aeChartValueMode}
            todayMonthKey={today.monthKey}
            aeMonthlyTarget={state.aeMonthlyTarget}
          />
        )}
      </Card>

      {/* Floating right-side Inputs panel — admin-edited, stays visible while
          scrolling. Wrapped in a soft backdrop that closes on outside click,
          with Escape parity wired by useEscapeClose at the top of Dashboard.
          Phase B sub-PR 5: extracted to inputs-panel.jsx::InputsPanel. */}
      <InputsPanel
        open={inputsPanelOpen}
        onClose={() => setInputsPanelOpen(false)}
        lang={state.lang}
        state={state}
        setState={setState}
        dynamicArpu={dynamicArpu}
      />

      {/* Forecast table — extracted into forecast-table.jsx (Phase B sub-PR 3).
          Renders the entire Card section: header + body. Branches on
          timeframe.mode between MonthDetailCard grid (month/qtr) and the
          full <table> layout (fy/ytd). */}
      <ForecastTable
        state={state}
        setState={setState}
        t={t}
        lang={state.lang}
        rows={rows}
        visibleRows={visibleRows}
        today={today}
        actuals={actuals}
        timeframe={timeframe}
        mrrFmt={mrrFmt}
        tableEur={tableEur}
        tableValueMode={tableValueMode}
        setTableValueMode={setTableValueMode}
        tableExpanded={tableExpanded}
        setTableExpanded={setTableExpanded}
        sectionOpen={sections.table}
        onToggleSection={() => toggleSection('table')}
      />

      {/* Contratti — separate section below the forecast table. Always shown when HubSpot
          actuals contain deal records. Self-contained: own filters, own search, own sort. */}
      <Card padding="none" elevation="flat" collapsed={!sections.deals}
        className={`card ${sections.deals ? "" : "card--collapsed"}`}>
        <SectionHeader
          title={state.lang === "it" ? "Contratti" : "Contracts"}
          subtitle={actuals?.deals?.length ? `${actuals.deals.length} ${state.lang === "it" ? "deal in cache" : "cached deals"}` : null}
          open={sections.deals}
          onToggle={() => toggleSection("deals")}
        />
        {sections.deals && (
          <DealsView
            deals={actuals?.deals || []}
            owners={actuals?.owners || {}}
            portalId={actuals?.portalId || null}
            aes={state.aes || []}
            lang={state.lang}
            todayMonthKey={today.monthKey}
            todayQtrIdx={todayQtrIdx}
            QUARTERS={QUARTERS}
            dealsTimeframe={dealsTimeframe}
            setDealsTimeframe={setDealsTimeframe}
            fmtMoney={(n) => globalEur(n)}
          />
        )}
      </Card>

      <Card padding="none" elevation="flat" collapsed={!sections.notes}
        className={`card notes ${sections.notes ? "" : "card--collapsed"}`}>
        <SectionHeader
          title={t.notes}
          open={sections.notes}
          onToggle={() => toggleSection("notes")}
        />
        {sections.notes && (
        <ul>
          <li><b>Tot MRR EOM:</b> {state.lang === "it"
            ? "editabile. Drive di tutto: cambialo per ridisegnare il piano."
            : "editable. Drives everything: change it to redraw the plan."}</li>
          <li><b>Real EOM:</b> {state.lang === "it"
            ? "vuoto finché non inserisci almeno un actual del mese; poi è la somma di prevReal + Real Add − Churn."
            : "blank until you enter at least one actual for the month; then it's prevReal + Real Add − Churn."}</li>
          <li><b>Others:</b> {state.lang === "it"
            ? "Setup Upsell + Upsell Pipeline. Hover sulla cella per vedere i due valori; click per espandere."
            : "Setup Upsell + Upsell Pipeline. Hover the cell to see the two values; click to expand."}</li>
          <li><b>{state.lang === "it" ? "AE necessari" : "AEs needed"}:</b> {state.lang === "it"
            ? "residuo dopo GTM/Partner/Self/Others. Sotto: AE già hired, e quanto manca al target (e new-hire necessari)."
            : "residual after GTM/Partner/Self/Others. Below: hired AEs, and remaining gap to target (with extra hires)."}</li>
        </ul>
        )}
      </Card>

      <footer className="foot">
        <span>fonio.ai · sales planning</span>
        <span className="foot__sep"> · </span>
        <span className="foot__disclaimer">{state.lang === "it" ? "Solo per uso interno · dati riservati" : "For internal use only · confidential"}</span>
      </footer>
      <ScrollToTop />
    </div>
    </EditModeContext.Provider>
  );
}

// Fullscreen password gate shown before the dashboard loads (when HubSpot integration is enabled).
// Includes a 3-strike cooldown to discourage casual brute-force attempts (15s lockout).
function PasswordGate({ onSubmit, authError }) {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [wrongCount, setWrongCount] = useState(() => {
    try { return parseInt(localStorage.getItem("forecast_pw_strikes") || "0", 10); } catch { return 0; }
  });
  const [cooldownUntil, setCooldownUntil] = useState(() => {
    try { return parseInt(localStorage.getItem("forecast_pw_cooldown") || "0", 10); } catch { return 0; }
  });
  const [tick, setTick] = useState(0);
  // Tick every second when in cooldown to update the countdown UI.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setTick(t => t + 1), 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);
  // Reset strike count after a wrong attempt is shown
  useEffect(() => {
    if (authError === "view_invalid") {
      const newCount = wrongCount + 1;
      setWrongCount(newCount);
      try { localStorage.setItem("forecast_pw_strikes", String(newCount)); } catch {}
      if (newCount >= 3) {
        const until = Date.now() + 15_000;
        setCooldownUntil(until);
        try { localStorage.setItem("forecast_pw_cooldown", String(until)); } catch {}
      }
      setSubmitting(false);
    }
  }, [authError]);
  const remainingMs = cooldownUntil - Date.now();
  const inCooldown = remainingMs > 0;
  const submit = (e) => {
    e?.preventDefault();
    if (!pw || inCooldown) return;
    setSubmitting(true);
    onSubmit(pw);
  };
  return (
    <div className="pw-gate">
      <div className="pw-gate__card">
        <div className="logo" style={{ marginBottom: 14 }}>
          <span className="logo__dot" />
          <span className="logo__name">fonio AI</span>
          <span className="logo__sep">/</span>
          <span className="logo__title">Sales Forecast</span>
        </div>
        <h2 className="pw-gate__title">Accesso</h2>
        <p className="pw-gate__sub">Inserisci la password per visualizzare la dashboard.</p>
        <form onSubmit={submit}>
          <input autoFocus type="password" className="pw-gate__input" placeholder="Password"
            value={pw} onChange={(e) => setPw(e.target.value)} disabled={inCooldown} />
          {authError === "view_invalid" && !inCooldown && (
            <div className="pw-gate__err">Password errata. Riprova{wrongCount >= 2 ? ` (${3 - wrongCount} tentativi rimasti prima del cooldown)` : ""}.</div>
          )}
          {inCooldown && (
            <div className="pw-gate__err">
              Troppi tentativi. Riprova fra {Math.ceil(remainingMs / 1000)}s.
            </div>
          )}
          <button type="submit" className="pw-gate__btn" disabled={submitting || !pw || inCooldown}>
            {inCooldown ? `Bloccato ${Math.ceil(remainingMs / 1000)}s` : submitting ? "Verifica…" : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Assump + AvgBasketInput — moved to inputs-panel.jsx (Phase B sub-PR 5).
// Both are used only inside <InputsPanel>; their callers in app.jsx are gone.

// EditableText / MixBar / MomBadge / BoundChannelCell / OthersCell / MonthRow / MonthDetailCard moved to forecast-table.jsx (Phase B sub-PR 3)

// Error Boundary — catches any React render exception and shows a recovery screen instead
// of a blank page. Without this, a single bad row of data (e.g. malformed Date) would brick
// the whole app for the user. Critical for stability.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, info: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("Dashboard crashed:", error, info);
    this.setState({ info });
    try {
      localStorage.setItem("forecast_last_error", JSON.stringify({
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        componentStack: info?.componentStack || "",
        time: new Date().toISOString(),
        url: location.href,
      }));
    } catch {}
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="error-boundary">
        <div className="error-boundary__card">
          <div className="error-boundary__icon">⚠</div>
          <h2>Qualcosa è andato storto</h2>
          <p className="error-boundary__msg">{String(this.state.error?.message || this.state.error)}</p>
          <div className="error-boundary__actions">
            {/* ErrorBoundary lives outside the Dashboard scope so primitives.jsx
                window.Button isn't trivially in scope as JSX. Render the new
                .btn class system directly — same visual outcome. */}
            <button className="btn btn--ghost btn--md" onClick={() => location.reload()}>Ricarica</button>
            <button className="btn btn--danger btn--md" onClick={() => {
              try {
                const keep = ["forecast_view_pw", "forecast_worker_url"];
                const saved = {};
                for (const k of keep) saved[k] = localStorage.getItem(k);
                localStorage.clear();
                for (const [k, v] of Object.entries(saved)) if (v) localStorage.setItem(k, v);
              } catch {}
              location.reload();
            }}>Reset stato + ricarica</button>
          </div>
          <details className="error-boundary__details">
            <summary>Dettagli tecnici</summary>
            <pre>{String(this.state.error?.stack || this.state.error)}</pre>
            {this.state.info?.componentStack && <pre>{this.state.info.componentStack}</pre>}
          </details>
        </div>
      </div>
    );
  }
}

// Phase 1A migration — publish cross-referenced components to window.
// Under Babel-standalone, top-level function declarations were auto-globaled.
// Vite/esbuild does NOT do this; the legacy sibling files (forecast-table,
// kpi-strip, inputs-panel) reference these as bare identifiers in their JSX,
// which resolve via globalThis at render time.
{
  const W: any = typeof window !== "undefined" ? window : {};
  W.useCanEdit          = useCanEdit;       // legacy forecast-table reads via window
  W.EditableNumber      = EditableNumber;
  W.SectionHeader       = SectionHeader;
  W.InfoIcon            = InfoIcon;
  W.Tooltip             = Tooltip;
  W.Modal               = Modal;
  W.Sparkline           = Sparkline;
  W.Kpi                 = Kpi;
  // (W.StickyHeaderStat removed in Phase C2 — component deleted.)
  W.ScrollToTop         = ScrollToTop;
  W.GrowthChart         = GrowthChart;
  W.MultiSelectDropdown = MultiSelectDropdown;
  W.NotificationBell    = NotificationBell;
  // DealPopover + DealsView relocated to composed/ in Phase 4 sub-step 5.
  // Republish DealsView (imported above) for any window-globaled callers;
  // DealPopover isn't referenced from window anywhere, so dropped.
  W.DealsView           = DealsView;
  W.MonthFocusChart     = MonthFocusChart;
  W.PasswordGate        = PasswordGate;
  W.ErrorBoundary       = ErrorBoundary;
  W.Dashboard           = Dashboard;
}

// ReactDOM.createRoot(...) mount call moved to src/main.tsx so React Router
// can wrap the legacy view. Dashboard + ErrorBoundary exported for the router.
export { Dashboard, ErrorBoundary };
